import { useRef, useState, useEffect } from "react";
import usePersistentPageState from "../hooks/usePersistentPageState";
import { BASE_URL } from "../config";
import Layout from "./Layout";
import usePageMeta from "../hooks/usePageMeta";
import { useAppPermissions } from "../context/PermissionContext";
import ActionDropdown from "../components/ActionDropdown";
import { MdDragHandle, MdPlayCircle } from "react-icons/md";
import { FaFilter, FaSlidersH, FaSyncAlt, FaTimes, FaChevronDown } from "react-icons/fa";
import { Editor } from "primereact/editor";
import OfficeModal from "../components/forms/OfficeModal";
import { showSuccess, showError, showConfirm } from "../utils/dialog";
import { useAppTimezone } from "../context/AppTimezoneContext";
import { DEFAULT_APP_TIMEZONE } from "../utils/appDateTime";
import TimezoneSelect from "../components/TimezoneSelect";

import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  AdminSettingsSkillsFilterFields,
  buildAdminSettingsSkillsFilterChips,
  AdminSettingsCertificationsFilterFields,
  buildAdminSettingsCertificationsFilterChips,
  AdminSettingsPositionsFilterFields,
  buildAdminSettingsPositionsFilterChips,
  AdminSettingsDivisionsFilterFields,
  buildAdminSettingsDivisionsFilterChips,
  AdminSettingsOfficesFilterFields,
  buildAdminSettingsOfficesFilterChips,
  AdminSettingsAppNotesFilterFields,
  buildAdminSettingsAppNotesFilterChips,
  SETTINGS_SORT_OPTIONS,
} from "../components/filters";
import { AtlasListToolbar } from "../components/list-toolbar";
import {
  AtlasPageHeader,
  AtlasPageDivider,
  AtlasTabBar,
} from "../components/page-layout";

const EMPTY_ARRAY = [];

// Skill Modal Component
const SkillModal = ({ skill, divisions = [], onClose, onSave }) => {
  const [form, setForm] = useState({
    name: skill?.name || "",
    division_ids: skill?.division_ids
      ? skill.division_ids.split(",").filter(Boolean)
      : [],
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return showError("Name is required");

    try {
      setSaving(true);
      const url = skill
        ? `${BASE_URL}/api/admin/skills/${skill.id}`
        : `${BASE_URL}/api/admin/skills`;

      const method = skill ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save skill");

      showSuccess(
        skill ? "Skill updated successfully" : "Skill created successfully"
      );
      onSave();
      onClose();
    } catch (e) {
      showError("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleDivision = (id) => {
    setForm((prev) => ({
      ...prev,
      division_ids: prev.division_ids.includes(id)
        ? prev.division_ids.filter((d) => d !== id)
        : [...prev.division_ids, id],
    }));
  };

  //   //!refs
  //  const signinStatusDropdownRef = useRef(null);

  // //!check box handler for signin status filter
  // const handleSigninStatusChange = (status) => {
  //   setSelectedSigninStatuses((prev) =>
  //     prev.includes(status)
  //       ? prev.filter((s) => s !== status)
  //       : [...prev, status]
  //   );
  // };
  // useEffect(() => {
  //    const handleClickOutside = (event) => {
  //      if (
  //        signinStatusDropdownRef.current &&
  //        !signinStatusDropdownRef.current.contains(event.target)
  //      ) {
  //        setShowSigninStatusDropdown(false);
  //      }
  //    };

  //    document.addEventListener("mousedown", handleClickOutside);
  //    return () => document.removeEventListener("mousedown", handleClickOutside);
  //  }, []);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center px-3"
      style={{ zIndex: 99999 }}
    >
      <div className="bg-card text-foreground rounded-lg shadow w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-lg font-semibold">
            {skill ? "Edit Skill" : "Create Skill"}
          </h3>
          <button
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            ✖
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 space-y-4 overflow-y-auto max-h-[65vh]"
        >
          <div>
            <label className="block text-sm font-medium mb-2">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
              placeholder="Enter skill name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Divisions</label>
            <div className="border border-border rounded-lg p-3 max-h-64 overflow-y-auto">
              {divisions.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  No divisions available
                </span>
              ) : (
                <div className="space-y-2">
                  {divisions.map((div) => (
                    <label
                      key={div.division_id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form.division_ids.includes(
                          div.division_id.toString()
                        )}
                        onChange={() =>
                          toggleDivision(div.division_id.toString())
                        }
                        className="accent-[#DC1F00]"
                      />
                      <span>{div.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              type="submit"
              className="px-4 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-lg disabled:opacity-50"
            >
              {saving ? "Saving..." : skill ? "Update" : "Create"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary"
            >
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Settings = () => {
  usePageMeta("Settings");
  const { canWriteAdmin } = useAppPermissions();
  const adminReadOnly = !canWriteAdmin;
  const filterPanel = useFilterPanel();
  const hiddenSigninTabId = "SYSTEM SETTINGS";
  const [showSigninSettingsTab, setShowSigninSettingsTab] = useState(false);
  const deletePressCountRef = useRef(0);
  const [signin2faEnabled, setSignin2faEnabled] = useState(false);
  const [signinSettingsLoading, setSigninSettingsLoading] = useState(false);
  const [signinSettingsSaving, setSigninSettingsSaving] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(DEFAULT_APP_TIMEZONE);
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const [hstRate, setHstRate] = useState(13);
  const [hstRateSaving, setHstRateSaving] = useState(false);
  const { refreshTimezone } = useAppTimezone();

  const tabs = [
    "SKILLS",
    "CERTIFICATIONS",
    "POSITIONS",
    "DIVISIONS",
    "OFFICES",
    "APP NOTES",
    ...(showSigninSettingsTab
      ? [{ id: hiddenSigninTabId, label: "SYSTEM" }]
      : []),
  ];

  const [selectedTab, setSelectedTab] = useState("SKILLS");
  const [innerBtn, setInnerBtn] = useState("Pages");

  // -- TAB-SPECIFIC FILTERS (persisted across navigation) --
  const [tabSearchTerms, setTabSearchTerms] = usePersistentPageState(
    "page_search_settings",
    {}
  );
  const [tabDivisionFilters, setTabDivisionFilters] = usePersistentPageState(
    "page_division_filters_settings",
    {}
  );
  const [tabShowDeleted, setTabShowDeleted] = usePersistentPageState(
    "page_show_deleted_settings",
    {}
  );
  const [tabCurrentPages, setTabCurrentPages] = useState({});

  const searchTerm = tabSearchTerms[selectedTab] || "";
  const setSearchTerm = (val) => setTabSearchTerms(prev => ({
    ...prev,
    [selectedTab]: typeof val === 'function' ? val(prev[selectedTab] || "") : val
  }));

  const divisionFilter = tabDivisionFilters[selectedTab] || EMPTY_ARRAY;
  const setDivisionFilter = (val) => setTabDivisionFilters(prev => ({
    ...prev,
    [selectedTab]: typeof val === 'function' ? val(prev[selectedTab] || EMPTY_ARRAY) : val
  }));

  const showDeleted = !!tabShowDeleted[selectedTab];
  const setShowDeleted = (val) => setTabShowDeleted(prev => ({
    ...prev,
    [selectedTab]: typeof val === 'function' ? val(!!prev[selectedTab]) : val
  }));

  const currentPage = tabCurrentPages[selectedTab] || 1;
  const setCurrentPage = (val) => setTabCurrentPages(prev => ({
    ...prev,
    [selectedTab]: typeof val === 'function' ? val(prev[selectedTab] || 1) : val
  }));

  // Data for APP NOTES
  const [appNotes, setAppNotes] = useState([]);
  const [appNotesTotal, setAppNotesTotal] = useState(0);
  const [appNotesLoading, setAppNotesLoading] = useState(false);
  const [appNotesError, setAppNotesError] = useState("");
  const [appNotesSortKey, setAppNotesSortKey] = useState("name");
  const [appNotesSortDir, setAppNotesSortDir] = useState("asc");
  const [showCreateAppNote, setShowCreateAppNote] = useState(false);
  const [showEditAppNote, setShowEditAppNote] = useState(false);
  const [editingAppNote, setEditingAppNote] = useState(null);
  //!
  // Signins sorting state
  const [signinsSortKey, setSigninsSortKey] = useState("signin_date");
  const [signinsSortDir, setSigninsSortDir] = useState("desc");
  //!
  const [showSigninStatusDropdown, setShowSigninStatusDropdown] =
    useState(false);
  const [selectedSigninStatuses, setSelectedSigninStatuses] = usePersistentPageState("page_signin_statuses_settings", []);

  const isSigninSettingsTab = selectedTab === hiddenSigninTabId;

  const signinStatusDropdownRef = useRef(null);

  //!check box handler for signin status filter
  // const handleSigninStatusChange = (status) => {
  //   setSelectedSigninStatuses((prev) =>
  //     prev.includes(status)
  //       ? prev.filter((s) => s !== status)
  //       : [...prev, status]
  //   );
  // };
  const handleSigninStatusChange = (status) => {
    setSelectedSigninStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        signinStatusDropdownRef.current &&
        !signinStatusDropdownRef.current.contains(event.target)
      ) {
        setShowSigninStatusDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Delete") return;
      deletePressCountRef.current += 1;
      if (deletePressCountRef.current >= 5) {
        setShowSigninSettingsTab(true);
        deletePressCountRef.current = 0;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      "";
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  };

  const loadSigninSettings = async () => {
    try {
      setSigninSettingsLoading(true);
      const headers = getAuthHeaders();

      const [signinRes, timezoneRes, hstRes] = await Promise.all([
        fetch(`${BASE_URL}/api/admin/signin-settings`, {
          credentials: "include",
          headers,
        }),
        fetch(`${BASE_URL}/api/config/app-timezone`, {
          credentials: "include",
          headers,
        }),
        fetch(`${BASE_URL}/api/admin/hst-settings`, {
          credentials: "include",
          headers,
        }),
      ]);

      const signinData = await signinRes.json().catch(() => ({}));
      if (!signinRes.ok || !signinData.success) {
        throw new Error(signinData.message || "Failed to load sign-in settings");
      }
      setSignin2faEnabled(!!signinData.data?.signin2faEnabled);

      const timezoneData = await timezoneRes.json().catch(() => ({}));
      if (timezoneRes.ok && timezoneData.success) {
        setSelectedTimezone(
          timezoneData.data?.timezone || DEFAULT_APP_TIMEZONE,
        );
      }

      const hstData = await hstRes.json().catch(() => ({}));
      if (hstRes.ok && hstData.success) {
        setHstRate(hstData.data?.hstRate ?? 13);
      }
    } catch (e) {
      showError(e.message || "Failed to load sign-in settings");
    } finally {
      setSigninSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (showSigninSettingsTab) {
      loadSigninSettings();
    }
  }, [showSigninSettingsTab]);

  const saveSigninSettings = async (enabled) => {
    try {
      setSigninSettingsSaving(true);
      const url = `${BASE_URL}/api/admin/signin-settings`;
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("token") ||
        "";
      const res = await fetch(url, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ signin2faEnabled: enabled }),
      });
      const raw = await res.text();
      const data = raw ? JSON.parse(raw) : {};
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to update sign-in settings");
      }
      setSignin2faEnabled(enabled);
      showSuccess(`System-wide sign-in 2FA ${enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      showError(e.message || "Failed to update sign-in settings");
    } finally {
      setSigninSettingsSaving(false);
    }
  };

  const saveTimezoneSettings = async () => {
    try {
      setTimezoneSaving(true);
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("token") ||
        "";
      const res = await fetch(`${BASE_URL}/api/config/app-timezone`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ timezone: selectedTimezone }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to update timezone");
      }
      await refreshTimezone();
      showSuccess("Display timezone updated successfully");
    } catch (e) {
      showError(e.message || "Failed to update timezone");
    } finally {
      setTimezoneSaving(false);
    }
  };

  const saveHstSettings = async () => {
    try {
      setHstRateSaving(true);
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("token") ||
        "";
      const res = await fetch(`${BASE_URL}/api/admin/hst-settings`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ hstRate: parseFloat(hstRate) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to update HST rate");
      }
      showSuccess("HST tax rate updated successfully");
    } catch (e) {
      showError(e.message || "Failed to update HST rate");
    } finally {
      setHstRateSaving(false);
    }
  };

  // Data for OFFICES
  const [offices, setOffices] = useState([]);
  const [officesTotal, setOfficesTotal] = useState(0);
  const [officesLoading, setOfficesLoading] = useState(false);
  const [officesError, setOfficesError] = useState("");
  const [officesSortKey, setOfficesSortKey] = useState("name");
  const [officesSortDir, setOfficesSortDir] = useState("asc");

  // Pagination state
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);
  const [showSortKeyDropdown, setShowSortKeyDropdown] = useState(false);

  // Filter states for each tab
  const [skillsFilterBy, setSkillsFilterBy] = usePersistentPageState("page_skills_filter_settings", "all");

  const [positionsFilterBy, setPositionsFilterBy] = usePersistentPageState("page_positions_filter_settings", "all");
  const [divisionsFilterBy, setDivisionsFilterBy] = usePersistentPageState("page_divisions_filter_settings", "all");
  const [officesFilterBy, setOfficesFilterBy] = usePersistentPageState("page_offices_filter_settings", "all");
  const [appNotesFilterBy, setAppNotesFilterBy] = usePersistentPageState("page_app_notes_filter_settings", "all");

  // Sorting state for Skills
  const [skillsSortKey, setSkillsSortKey] = useState("name");
  const [skillsSortDir, setSkillsSortDir] = useState("asc");

  // Data for SKILLS
  const [skills, setSkills] = useState([]);
  const [skillsTotal, setSkillsTotal] = useState(0);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillsError, setSkillsError] = useState("");
  const [showFilterPanel, setShowFilterPanel] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Normalize type strings returned from the server into canonical labels
  const normalizeTypes = (raw) => {
    if (!raw && raw !== 0) return "";
    // Accept arrays (server might return array) or strings
    let s = Array.isArray(raw) ? raw.join(",") : String(raw || "");
    // Turn camelCase into space-separated: checkIn -> check In
    s = s.replace(/([a-z])([A-Z])/g, "$1 $2");
    // Replace underscores/dashes/pipes with spaces
    s = s.replace(/[_\-|]+/g, " ");
    // Split on commas/semicolons and trim
    const parts = s
      .split(/[,;]+/)
      .map((p) => p.trim())
      .filter(Boolean);

    const mapToken = (t) => {
      const x = t.toLowerCase().replace(/\s+/g, " ").trim();
      if (x.includes("check in early") || x.includes("checkinearly"))
        return "Check in early";
      if (x.includes("check in") || x === "checkin") return "Check in";
      if (x.includes("check out late") || x.includes("checkoutlate"))
        return "Check out late";
      if (x.includes("check out") || x === "checkout") return "Check out";
      // Fallback: capitalize each word
      return t
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
    };

    const seen = new Set();
    const out = [];
    for (const p of parts) {
      const m = mapToken(p);
      if (!seen.has(m)) {
        seen.add(m);
        out.push(m);
      }
    }
    return out.join(", ");
  };

  const mapNotes = (notes) => {
    if (!Array.isArray(notes)) return [];
    return notes.map((n) => {
      const normalized = normalizeTypes(
        n.app_pages || n.type || n.appPages || ""
      );
      // Backend returns `id` for checkin_note_id; many frontend parts expect `app_note_id`.
      const app_note_id =
        n.app_note_id || n.checkin_note_id || n.id || n.ID || null;
      return { ...n, app_note_id, normalized_types: normalized };
    });
  };

  // Data for CERTIFICATIONS
  const [certifications, setCertifications] = useState([]);
  const [certificationsTotal, setCertificationsTotal] = useState(0);
  const [certificationsLoading, setCertificationsLoading] = useState(false);
  const [certificationsError, setCertificationsError] = useState("");
  const [certificationsSortKey, setCertificationsSortKey] = useState("name");
  const [certificationsSortDir, setCertificationsSortDir] = useState("asc");

  const [certificationsFilterBy, setCertificationsFilterBy] = useState("all");

  // Data for POSITIONS
  const [positions, setPositions] = useState([]);
  const [positionsTotal, setPositionsTotal] = useState(0);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState("");
  const [positionsSortKey, setPositionsSortKey] = useState("name");
  const [positionsSortDir, setPositionsSortDir] = useState("asc");

  // Data for DIVISIONS
  const [divisionsList, setDivisionsList] = useState([]);
  const [divisionsTotal, setDivisionsTotal] = useState(0);
  const [divisionsLoading, setDivisionsLoading] = useState(false);
  const [divisionsError, setDivisionsError] = useState("");
  const [divisionsSortKey, setDivisionsSortKey] = useState("name");
  const [divisionsSortDir, setDivisionsSortDir] = useState("asc");

  // Data for dropdowns
  const [divisions, setDivisions] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [availableCertifications, setAvailableCertifications] = useState([]);
  const [clients, setClients] = useState([]);

  // Load dropdown data (divisions, clients, skills) for modals
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/divisions`, {
          credentials: "include",
        });
        if (res.ok) {
          const j = await res.json();
          setDivisions(j.data || []);
        } else {
          console.warn("Failed to load admin divisions", res.status);
        }
      } catch (e) {
        console.error("Error loading admin divisions", e);
      }
    };

    loadDropdowns();
  }, []);

  // Modal states
  const [showCreateSkill, setShowCreateSkill] = useState(false);
  const [showEditSkill, setShowEditSkill] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [showCreateOffice, setShowCreateOffice] = useState(false);
  const [showEditOffice, setShowEditOffice] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [showCreateCertification, setShowCreateCertification] = useState(false);
  const [showEditCertification, setShowEditCertification] = useState(false);
  const [editingCertification, setEditingCertification] = useState(null);
  const [showCreatePosition, setShowCreatePosition] = useState(false);
  const [showEditPosition, setShowEditPosition] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [showCreateDivision, setShowCreateDivision] = useState(false);
  const [showEditDivision, setShowEditDivision] = useState(false);
  const [editingDivision, setEditingDivision] = useState(null);

  // Load available skills and certifications for Position modal dropdowns
  useEffect(() => {
    const loadAvailableLookups = async () => {
      // Only load when position modal is about to be shown or when filters change
      if (!showCreatePosition && !showEditPosition) return;
      try {
        // We want ALL available skills/certs for the dropdown, so we Do NOT filter by searchTerm here.
        const q = "";
        const divIds = getDivisionIdsCsv();
        const [skillsRes, certsRes] = await Promise.all([
          fetch(
            `${BASE_URL}/api/admin/skills?page=1&limit=10000&search=${q}&division=${encodeURIComponent(divIds)}&showDeleted=${showDeleted}`,
            { credentials: "include" }
          ),
          fetch(
            `${BASE_URL}/api/admin/certifications?page=1&limit=10000&search=${q}&division=${encodeURIComponent(divIds)}&showDeleted=${showDeleted}`,
            { credentials: "include" }
          ),
        ]);

        if (skillsRes.ok) {
          const sJson = await skillsRes.json();
          setAvailableSkills(sJson.data || []);
        } else {
          console.warn("Failed to load available skills", skillsRes.status);
          setAvailableSkills([]);
        }

        if (certsRes.ok) {
          const cJson = await certsRes.json();
          setAvailableCertifications(cJson.data || []);
        } else {
          console.warn(
            "Failed to load available certifications",
            certsRes.status
          );
          setAvailableCertifications([]);
        }
      } catch (e) {
        console.error("Error loading available skills/certifications", e);
        setAvailableSkills([]);
        setAvailableCertifications([]);
      }
    };

    loadAvailableLookups();
  }, [
    showCreatePosition,
    showEditPosition,
    divisionFilter,
    showDeleted,
    BASE_URL,
  ]);

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  //! Handlers for new filter inputs
  const handleDivisionChange = (val) => {
    const normalized = Array.isArray(val)
      ? [...new Set(val.map((item) => String(item)).filter(Boolean))]
      : [];
    setDivisionFilter(normalized);
  };

  //!  Close filter panel automatically when switching tabs
  useEffect(() => {
    setShowFilterPanel(null);
  }, [selectedTab]);

  const handleShowDeletedChange = (e) => {
    setShowDeleted(e.target.checked);
  };
  //! fetch urls for each tab
  //! Load skills
  useEffect(() => {
    const fetchSkills = async () => {
      if (selectedTab !== "SKILLS") return;
      setSkillsLoading(true);
      const divIds = getDivisionIdsCsv();
      const res = await fetch(
        `${BASE_URL}/api/admin/skills?page=${currentPage}&limit=${pageSize}&search=${encodeURIComponent(
          searchTerm || ""
        )}&division=${encodeURIComponent(
          divIds
        )}&showDeleted=${showDeleted}&sort=${encodeURIComponent(
          skillsSortKey
        )}&dir=${encodeURIComponent(skillsSortDir)}`,
        { credentials: "include" }
      );

      const data = await res.json();
      setSkills(data.data || []);
      setSkillsTotal(data.total || 0);
      setTotalPages(Math.max(1, Math.ceil((data.total || 0) / pageSize)));
      setSkillsLoading(false);
    };
    fetchSkills();
  }, [
    selectedTab,
    currentPage,
    pageSize,
    searchTerm,
    skillsSortKey,
    skillsSortDir,
    divisionFilter,
    showDeleted,
    refreshTick,
  ]);
  //! fetch  url certifications
  useEffect(() => {
    const load = async () => {
      // Run when Certifications tab is active OR when the Create/Edit Position modal is open
      if (
        selectedTab !== "CERTIFICATIONS" &&
        !showCreatePosition &&
        !showEditPosition
      )
        return;

      try {
        setCertificationsLoading(true);
        setCertificationsError("");

        const q = encodeURIComponent(searchTerm || "");
        const divIds = getDivisionIdsCsv();
        // ✅ Clean and consistent query with all filters
        const res = await fetch(
          `${BASE_URL}/api/admin/certifications?page=${currentPage}&limit=${pageSize}&search=${q}` +
          `&division=${encodeURIComponent(divIds)}` +
          `&showDeleted=${showDeleted}` +
          `&sort=${encodeURIComponent(certificationsSortKey || "name")}` +
          `&dir=${encodeURIComponent(certificationsSortDir || "asc")}`,
          { credentials: "include" }
        );

        if (!res.ok)
          throw new Error(`Failed to load certifications (${res.status})`);

        const data = await res.json();

        setCertifications(data.data || []);
        setCertificationsTotal(data.total || 0);
        setTotalPages(Math.max(1, Math.ceil((data.total || 0) / pageSize)));
      } catch (e) {
        console.error("Error loading certifications:", e);
        setCertificationsError(e.message || "Failed to load certifications");
      } finally {
        setCertificationsLoading(false);
      }
    };

    load();
  }, [
    // 👇 dependencies that trigger reload when changed
    selectedTab,
    currentPage,
    pageSize,
    searchTerm,
    certificationsSortKey,
    certificationsSortDir,
    divisionFilter,
    showDeleted,
    showCreateCertification,
    showEditCertification,
    refreshTick,
  ]);

  //! Load positions
  useEffect(() => {
    const load = async () => {
      if (selectedTab !== "POSITIONS") return;

      try {
        setPositionsLoading(true);
        setPositionsError("");

        const q = encodeURIComponent(searchTerm || "");
        const divIds = getDivisionIdsCsv();

        const res = await fetch(
          `${BASE_URL}/api/admin/positions?page=${currentPage}&limit=${pageSize}&search=${q}` +
          `&division=${encodeURIComponent(divIds)}` +
          // `&status=${encodeURIComponent(positionsFilterBy || "all")}` +
          `&showDeleted=${showDeleted}` +
          `&sort=${encodeURIComponent(positionsSortKey || "name")}` +
          `&dir=${encodeURIComponent(positionsSortDir || "asc")}`,
          { credentials: "include" }
        );

        if (!res.ok)
          throw new Error(`Failed to load positions (${res.status})`);

        const data = await res.json();

        setPositions(data.data || []);
        setPositionsTotal(data.total || 0);
        setTotalPages(Math.max(1, Math.ceil((data.total || 0) / pageSize)));
      } catch (e) {
        setPositionsError(e.message || "Failed to load positions");
      } finally {
        setPositionsLoading(false);
      }
    };

    load();
  }, [
    selectedTab,
    currentPage,
    searchTerm,
    positionsSortKey,
    positionsSortDir,
    positionsFilterBy,
    divisionFilter,
    showDeleted,
    pageSize,
    showCreatePosition,
    showEditPosition,
    refreshTick,
  ]);

  // Load offices
  useEffect(() => {
    const load = async () => {
      if (selectedTab !== "OFFICES") return;

      try {
        setOfficesLoading(true);
        setOfficesError("");

        const q = encodeURIComponent(searchTerm || "");

        const res = await fetch(
          `${BASE_URL}/api/admin/offices?page=${currentPage}&limit=${pageSize}&search=${q}&showDeleted=${showDeleted}&sort=${encodeURIComponent(
            officesSortKey
          )}&dir=${encodeURIComponent(officesSortDir)}`,
          { credentials: "include" }
        );

        if (!res.ok) throw new Error(`Failed to load offices (${res.status})`);

        const data = await res.json();

        setOffices(data.data || []);
        setOfficesTotal(data.total || 0);
        setTotalPages(Math.max(1, Math.ceil((data.total || 0) / pageSize)));
      } catch (e) {
        setOfficesError(e.message || "Failed to load offices");
      } finally {
        setOfficesLoading(false);
      }
    };

    load();
  }, [
    selectedTab,
    currentPage,
    searchTerm,
    officesSortKey,
    officesSortDir,
    showDeleted,
    pageSize,
    refreshTick,
  ]);

  //! Load divisions
  useEffect(() => {
    const load = async () => {
      if (selectedTab !== "DIVISIONS") return;
      try {
        setDivisionsLoading(true);
        setDivisionsError("");
        const q = encodeURIComponent(searchTerm || "");
        const res = await fetch(
          `${BASE_URL}/api/admin/divisions-list?page=${currentPage}&limit=${pageSize}&search=${q}&showDeleted=${showDeleted}&sort=${encodeURIComponent(
            divisionsSortKey
          )}&dir=${encodeURIComponent(divisionsSortDir)}`,
          { credentials: "include" }
        );
        const text = await res.text();
        if (!res.ok)
          throw new Error(`Failed to load divisions (${res.status})`);
        let j;
        try {
          j = JSON.parse(text);
        } catch (_) {
          throw new Error("Invalid response from server");
        }
        setDivisionsList(j.data || []);
        setDivisionsTotal(j.total || 0);
        setTotalPages(Math.max(1, Math.ceil((j.total || 0) / pageSize)));
      } catch (e) {
        setDivisionsError(e.message || "Failed to load divisions");
      } finally {
        setDivisionsLoading(false);
      }
    };
    load();
  }, [
    selectedTab,
    currentPage,
    searchTerm,
    divisionsSortKey,
    divisionsSortDir,
    showDeleted,
    pageSize,
    refreshTick,
  ]);

  //!load app note
  useEffect(() => {
    const load = async () => {
      if (selectedTab !== "APP NOTES") return;

      try {
        setAppNotesLoading(true);
        setAppNotesError("");

        const q = encodeURIComponent(searchTerm || "");
        const status = encodeURIComponent(
          selectedSigninStatuses.join(",") || ""
        );

        const res = await fetch(
          `${BASE_URL}/api/admin/app-notes?page=${currentPage}&limit=${pageSize}&search=${q}&status=${status}&showDeleted=${showDeleted}&sort=${encodeURIComponent(
            appNotesSortKey
          )}&dir=${encodeURIComponent(appNotesSortDir)}`,
          { credentials: "include" }
        );

        const text = await res.text();

        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          console.error("Failed to parse API response:", parseError);
          throw new Error("Invalid response format from server");
        }

        if (!res.ok)
          throw new Error(`Failed to load app notes (${res.status})`);

        if (!Array.isArray(data.data))
          throw new Error("Invalid data format from server");

        setAppNotes(mapNotes(data.data));
        setAppNotesTotal(data.total || 0);
        setTotalPages(Math.max(1, Math.ceil((data.total || 0) / pageSize)));
      } catch (e) {
        console.error("Error loading app notes:", e);
        setAppNotesError(e.message || "Failed to load app notes");
        setAppNotes([]);
        setAppNotesTotal(0);
        setTotalPages(1);
      } finally {
        setAppNotesLoading(false);
      }
    };

    load();
  }, [
    selectedTab,
    currentPage,
    searchTerm,
    appNotesSortKey,
    appNotesSortDir,
    selectedSigninStatuses,
    showDeleted,
    pageSize,
    refreshTick,
  ]);

  // Client-side sorting and filtering for Skills
  // Skills are fetched from the server with paging/filtering.
  // Use the server-provided page (`skills`) directly and derive total pages from `skillsTotal`.
  const sortedAndFilteredSkills = (() => {
    return skills;
  })();

  // The server already returns a single page in `skills`, so use it as-is for rendering.
  const paginatedSkills = skills;


  // Update total pages when filtered results change
  // useEffect(() => {
  //   const filtered = skills.filter((skill) => {
  //     const search = searchTerm.toLowerCase();
  //     return (
  //       !search ||
  //       (skill.name || "").toLowerCase().includes(search) ||
  //       (skill.division_name || "").toLowerCase().includes(search)
  //     );
  //   });
  //   setTotalPages(Math.max(1, Math.ceil(filtered.length / pageSize)));
  // }, [skills, searchTerm, pageSize]);

  useEffect(() => {
    setTotalPages(Math.max(1, Math.ceil((skillsTotal || 0) / pageSize)));
  }, [skillsTotal, pageSize]);


  // Handler functions
  const handleCreateCertification = () => {
    setEditingCertification(null);
    setShowCreateCertification(true);
  };

  const handleEditCertification = (cert) => {
    setEditingCertification(cert);
    setShowEditCertification(true);
  };

  const handleDeleteCertification = async (id) => {
    if (
      !(await showConfirm(
        "Are you sure you want to delete this certification?"
      ))
    )
      return;

    try {
      const res = await fetch(`${BASE_URL}/api/admin/certifications/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();

      if (!res.ok)
        throw new Error(data.message || "Failed to delete certification");

      // Reload certifications
      const q = encodeURIComponent(searchTerm || "");
      const divIds = getDivisionIdsCsv();
      const reloadRes = await fetch(
        `${BASE_URL}/api/admin/certifications?page=${currentPage}&limit=${pageSize}&search=${q}` +
        `&division=${encodeURIComponent(divIds)}` +
        `&showDeleted=${showDeleted}` +
        `&sort=${encodeURIComponent(certificationsSortKey)}` +
        `&dir=${encodeURIComponent(certificationsSortDir)}`,
        { credentials: "include" }
      );
      const reloadData = await reloadRes.json();
      setCertifications(reloadData.data || []);
      setCertificationsTotal(reloadData.total || 0);
    } catch (e) {
      showError("Failed to delete certification: " + e.message);
    }
  };

  const handleCreatePosition = () => {
    setEditingPosition(null);
    setShowCreatePosition(true);
  };

  const handleEditPosition = (position) => {
    setEditingPosition(position);
    setShowEditPosition(true);
  };

  const handleDeletePosition = async (id) => {
    if (!(await showConfirm("Are you sure you want to delete this position?")))
      return;

    try {
      const res = await fetch(`${BASE_URL}/api/admin/positions/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to delete position");

      // Reload positions
      const q = encodeURIComponent(searchTerm || "");
      const divIds = getDivisionIdsCsv();
      const reloadRes = await fetch(
        `${BASE_URL}/api/admin/positions?page=${currentPage}&limit=${pageSize}&search=${q}` +
        `&division=${encodeURIComponent(divIds)}` +
        `&showDeleted=${showDeleted}` +
        `&sort=${encodeURIComponent(positionsSortKey)}` +
        `&dir=${encodeURIComponent(positionsSortDir)}`,
        { credentials: "include" }
      );
      const reloadData = await reloadRes.json();
      setPositions(reloadData.data || []);
      setPositionsTotal(reloadData.total || 0);
    } catch (e) {
      showError("Failed to delete position: " + e.message);
    }
  };

  const handleCreateDivision = () => {
    setEditingDivision(null);
    setShowCreateDivision(true);
  };

  const handleEditDivision = (division) => {
    setEditingDivision(division);
    setShowEditDivision(true);
  };

  const handleDeleteDivision = async (id) => {
    if (!(await showConfirm("Are you sure you want to delete this division?")))
      return;

    try {
      const res = await fetch(`${BASE_URL}/api/admin/divisions/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to delete division");

      // Reload divisions
      const q = encodeURIComponent(searchTerm || "");
      const reloadRes = await fetch(
        `${BASE_URL}/api/admin/divisions-list?page=${currentPage}&limit=${pageSize}&search=${q}` +
        `&showDeleted=${showDeleted}` +
        `&sort=${encodeURIComponent(divisionsSortKey)}` +
        `&dir=${encodeURIComponent(divisionsSortDir)}`,
        { credentials: "include" }
      );
      const reloadData = await reloadRes.json();
      setDivisionsList(reloadData.data || []);
      setDivisionsTotal(reloadData.total || 0);
    } catch (e) {
      showError("Failed to delete division: " + e.message);
    }
  };

  // Sort arrow component
  const SortArrow = ({ colKey, activeKey, activeDir, onChange }) => (
    <button
      onClick={() => {
        const k = activeKey ?? skillsSortKey;
        const d = activeDir ?? skillsSortDir;
        if ((activeKey ?? skillsSortKey) === colKey) {
          const nextDir = d === "asc" ? "desc" : "asc";
          if (onChange) onChange(colKey, nextDir);
          else {
            setSkillsSortKey(colKey);
            setSkillsSortDir(nextDir);
          }
        } else {
          if (onChange) onChange(colKey, "asc");
          else {
            setSkillsSortKey(colKey);
            setSkillsSortDir("asc");
          }
        }
      }}
      className="float-right mr-1 text-sm text-white/90 hover:text-white"
      title="Sort"
    >
      {(activeKey ?? skillsSortKey) === colKey
        ? (activeDir ?? skillsSortDir) === "asc"
          ? "▲"
          : "▼"
        : "⇅"}
    </button>
  );

  const DropdownCheckbox = ({ label, options, selected, onChange }) => {
    const [open, setOpen] = useState(false);

    const normalizedOptions = Array.isArray(options)
      ? options.map((opt) =>
        typeof opt === "object" && opt !== null
          ? {
            value: String(opt.value ?? ""),
            label: String(opt.label ?? opt.value ?? ""),
          }
          : { value: String(opt), label: String(opt) }
      )
      : [];

    const uniqueOptions = normalizedOptions.filter(
      (opt, idx, arr) =>
        opt.value && arr.findIndex((candidate) => candidate.value === opt.value) === idx
    );

    const selectedValues = Array.isArray(selected)
      ? [...new Set(selected.map((val) => String(val)).filter(Boolean))]
      : [];

    const selectedSet = new Set(selectedValues);

    const optionLabelMap = uniqueOptions.reduce((acc, opt) => {
      acc[opt.value] = opt.label;
      return acc;
    }, {});

    const toggle = (value) => {
      const normalizedValue = String(value);
      const exists = selectedSet.has(normalizedValue);
      const updated = exists
        ? selectedValues.filter((v) => v !== normalizedValue)
        : [...selectedValues, normalizedValue];
      onChange(updated);
    };

    const toggleAll = () => {
      if (selectedValues.length === uniqueOptions.length) {
        onChange([]);
      } else {
        onChange(uniqueOptions.map((opt) => opt.value));
      }
    };

    const removeOne = (val) => {
      const normalizedValue = String(val);
      onChange(selectedValues.filter(item => item !== normalizedValue));
    };

    return (
      <div className="relative mb-4">
        <label className="block text-xs font-semibold text-gray-600 mb-1 tracking-wide uppercase">
          {label}
        </label>

        <div className="relative group">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className={`w-full border rounded-lg px-3 py-2 text-sm flex justify-between items-center transition-all bg-white hover:border-sidebar-active/50 focus:outline-none focus:ring-2 focus:ring-sidebar-active/20 ${open ? 'border-sidebar-active ring-2 ring-sidebar-active/20' : 'border-gray-200'
              }`}
          >
            <span className="text-gray-500 font-medium">
              {open ? 'Select options...' : `Select ${label}`}
            </span>
            <FaChevronDown className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180 text-sidebar-active' : ''}`} />
          </button>

          {open && (
            <>
              <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
              <div className="absolute z-[70] left-0 right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-2xl max-h-64 overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
                <div className="p-2 border-b border-gray-50 bg-gray-50/50">
                  <button
                    onClick={toggleAll}
                    className="w-full text-left px-3 py-1.5 text-xs font-bold text-sidebar-active hover:bg-white rounded-md transition-colors flex items-center justify-between"
                  >
                    {selectedValues.length === uniqueOptions.length ? "DESELECT ALL" : "SELECT ALL"}
                    <span className="bg-sidebar-active/10 px-1.5 py-0.5 rounded text-[10px]">
                      {selectedValues.length} / {uniqueOptions.length}
                    </span>
                  </button>
                </div>
                <div className="overflow-y-auto flex-1 py-1 custom-scrollbar">
                  {uniqueOptions.length > 0 ? (
                    uniqueOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-3 px-4 py-2 hover:bg-sidebar-active/5 cursor-pointer text-sm transition-colors group ${selectedSet.has(opt.value) ? 'bg-sidebar-active/[0.02]' : ''
                          }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${selectedSet.has(opt.value)
                          ? 'bg-sidebar-active border-sidebar-active'
                          : 'border-gray-300 bg-white group-hover:border-sidebar-active/50'
                          }`}>
                          {selectedSet.has(opt.value) && <FaTimes className="text-white text-[10px]" />}
                        </div>
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={selectedSet.has(opt.value)}
                          onChange={() => toggle(opt.value)}
                        />
                        <span className={`flex-1 truncate ${selectedSet.has(opt.value) ? 'font-semibold text-sidebar-active' : 'text-gray-700'}`}>
                          {opt.label}
                        </span>
                      </label>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-gray-400 text-xs italic">
                      No options available
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Selected Chips Section */}
        {selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {selectedValues.map(val => (
              <div
                key={val}
                className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 border border-gray-200 px-2.5 py-1 rounded-full text-xs font-medium animate-in fade-in slide-in-from-top-1 duration-200"
              >
                <span className="max-w-[120px] truncate">{optionLabelMap[val] || val}</span>
                <button
                  onClick={() => removeOne(val)}
                  className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                >
                  <FaTimes className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
            <button
              onClick={() => onChange([])}
              className="text-[10px] uppercase font-bold text-gray-400 hover:text-red-500 transition-colors px-1"
            >
              Clear
            </button>
          </div>
        )}
      </div>
    );
  };

  const SingleSelect = ({ label, options, value, onChange }) => (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-600 mb-1 tracking-wide uppercase">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm flex justify-between items-center transition-all bg-white hover:border-sidebar-active/50 focus:outline-none focus:ring-2 focus:ring-sidebar-active/20"
      >
        {Array.isArray(options) &&
          options.map((opt) => (
            <option key={opt.value || opt} value={opt.value || opt}>
              {opt.label || opt}
            </option>
          ))}
      </select>
    </div>
  );

  // Sort key labels for interactive summary
  const sortKeyLabels = {
    SKILLS: { name: "Name" },
    CERTIFICATIONS: { name: "Name" },
    POSITIONS: { name: "Name" },
    DIVISIONS: { name: "Name" },
    OFFICES: { name: "Name" },
    'APP NOTES': { name: "Name", usage: "Usage" }
  };

  const resolveDivisionLabel = (divisionValue) => {
    const match = divisions.find(
      (div) => String(div.division_id) === String(divisionValue)
    );
    return match?.name || String(divisionValue || "");
  };

  const getDivisionIdsCsv = () => {
    if (!Array.isArray(divisionFilter) || divisionFilter.length === 0) return "";

    const ids = divisionFilter.flatMap((value) => {
      if (value === null || value === undefined || value === "") return [];

      const raw = String(value).trim();
      if (!raw) return [];

      const asNumber = Number(raw);
      if (!Number.isNaN(asNumber)) return [String(asNumber)];

      const byName = divisions
        .filter((div) => String(div.name || "") === raw)
        .map((div) => String(div.division_id));

      return byName;
    });

    return [...new Set(ids)].join(",");
  };

  // Filter chip helpers
  const removeFilter = (key, value = null) => {
    if (key === 'search') setSearchTerm("");
    if (key === 'divisionFilter') {
      if (value !== null) {
        setDivisionFilter(prev => prev.filter(v => v !== value));
      } else {
        setDivisionFilter([]);
      }
    }
    if (key === 'showDeleted') setShowDeleted(false);
    if (key === 'signinStatus') {
      if (value !== null && value !== undefined) {
        setSelectedSigninStatuses(prev => prev.filter(s => s !== value));
      } else {
        setSelectedSigninStatuses([]);
      }
    }
  };

  const getAllActiveFilters = () => {
    const active = [];
    const summarizeValues = (values) => {
      if (!Array.isArray(values) || values.length === 0) return "";
      const cleaned = values.map((v) => String(v ?? "").trim()).filter(Boolean);
      if (cleaned.length === 0) return "";
      if (cleaned.length <= 2) return cleaned.join(",");
      return `${cleaned[0]},${cleaned[1]}..`;
    };

    if (searchTerm) active.push({ key: 'search', label: `Search: ${searchTerm}` });

    if (Array.isArray(divisionFilter) && divisionFilter.length > 0) {
      const divisionSummary = summarizeValues(
        divisionFilter.map((divId) => resolveDivisionLabel(divId))
      );
      if (divisionSummary) {
        active.push({ key: 'divisionFilter', label: `Division: ${divisionSummary}` });
      }
    }

    if (showDeleted) active.push({ key: 'showDeleted', label: "Showing Deleted" });

    if (selectedTab === "APP NOTES" && selectedSigninStatuses.length > 0) {
      const statusSummary = summarizeValues(selectedSigninStatuses);
      if (statusSummary) {
        active.push({ key: 'signinStatus', label: `Status: ${statusSummary}` });
      }
    }

    return active;
  };

  return (
    <Layout active="Settings">
      <fieldset
        disabled={adminReadOnly}
        className="border-0 m-0 p-0 min-w-0 flex flex-col flex-1 min-h-0"
      >
        {/* Create/Edit Skill Modal */}
        {(showCreateSkill || showEditSkill) && (
          <SkillModal
            skill={editingSkill}
            divisions={divisions}
            onClose={() => {
              setShowCreateSkill(false);
              setShowEditSkill(false);
              setEditingSkill(null);
            }}
            onSave={async () => {
              try {
                setSkillsLoading(true);
                const divIds = getDivisionIdsCsv();
                const res = await fetch(
                  `${BASE_URL}/api/admin/skills?page=${currentPage}&limit=${pageSize}&search=${encodeURIComponent(
                    searchTerm || ""
                  )}&division=${encodeURIComponent(divIds)}&showDeleted=${showDeleted}&sort=${encodeURIComponent(
                    skillsSortKey
                  )}&dir=${encodeURIComponent(skillsSortDir)}`,
                  { credentials: "include" }
                );
                const data = await res.json();
                if (!res.ok)
                  throw new Error(data.message || "Failed to reload skills");
                setSkills(data.data || []);
                setSkillsTotal(data.total || 0);
              } catch (e) {
                console.error("Failed to reload skills:", e);
              } finally {
                setSkillsLoading(false);
              }
            }}
          />
        )}
        <div className="py-3 px-2 flex flex-col min-h-screen">
          <AtlasPageHeader breadcrumb="Admin" title="Settings" showBack={false} className="mb-2" />
          <AtlasPageDivider />

          <div className="mt-6 mx-6 p-4 border-2 border-gray-200 rounded-xl h-full flex flex-col">
            <div className={`flex-1 flex flex-col min-h-0 min-w-0 transition-all duration-300 ${filterPanel.contentOffsetClass}`}>
              {/* Tabs */}
              <AtlasTabBar tabs={tabs} activeTab={selectedTab} onTabChange={setSelectedTab} />
              {selectedTab === "CONTENT" && (
                <div className="flex space-x-6 mb-4">
                  {["Pages", "Layout", "Widget"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setInnerBtn(tab)}
                      className={`py-2 px-8 text-sm font-medium ${innerBtn === tab
                        ? "bg-[#DC1F0033] text-[#DC1F00] rounded-3xl"
                        : "bg-[#00000033] text-[#00000080] rounded-3xl hover:bg-[#696969] hover:text-white"
                        }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              )}
              {!isSigninSettingsTab && (
                <AtlasListToolbar
                  searchValue={searchTerm}
                  onSearchChange={(value) => {
                    setSearchTerm(value);
                    setCurrentPage(1);
                  }}
                  searchPlaceholder={`Search ${selectedTab.toLowerCase()}...`}
                  onFilterClick={filterPanel.toggle}
                  filterOpen={filterPanel.isOpen}
                  onRefresh={() => setRefreshTick((t) => t + 1)}
                  refreshSpinning={skillsLoading || certificationsLoading || positionsLoading || officesLoading || appNotesLoading || divisionsLoading}
                  resultsMeta={{
                    startRow: (() => {
                      const count = selectedTab === "SKILLS" ? paginatedSkills.length :
                        selectedTab === "CERTIFICATIONS" ? certifications.length :
                          selectedTab === "POSITIONS" ? positions.length :
                            selectedTab === "DIVISIONS" ? divisionsList.length :
                              selectedTab === "OFFICES" ? offices.length :
                                appNotes.length;
                      return count === 0 ? 0 : (currentPage - 1) * pageSize + 1;
                    })(),
                    endRow: (() => {
                      const count = selectedTab === "SKILLS" ? paginatedSkills.length :
                        selectedTab === "CERTIFICATIONS" ? certifications.length :
                          selectedTab === "POSITIONS" ? positions.length :
                            selectedTab === "DIVISIONS" ? divisionsList.length :
                              selectedTab === "OFFICES" ? offices.length :
                                appNotes.length;
                      return (currentPage - 1) * pageSize + count;
                    })(),
                    totalCount: (() => {
                      return selectedTab === "SKILLS" ? skillsTotal :
                        selectedTab === "CERTIFICATIONS" ? certificationsTotal :
                          selectedTab === "POSITIONS" ? positionsTotal :
                            selectedTab === "DIVISIONS" ? divisionsTotal :
                              selectedTab === "OFFICES" ? officesTotal :
                                appNotesTotal;
                    })(),
                    pageSize: pageSize,
                    pageSizeOptions: [15, 25, 50, 100, 200],
                    onPageSizeChange: (size) => {
                      setPageSize(size);
                      setCurrentPage(1);
                    },
                    sortBy: selectedTab === "SKILLS" ? skillsSortKey :
                      selectedTab === "CERTIFICATIONS" ? certificationsSortKey :
                        selectedTab === "POSITIONS" ? positionsSortKey :
                          selectedTab === "DIVISIONS" ? divisionsSortKey :
                            selectedTab === "OFFICES" ? officesSortKey :
                              appNotesSortKey,
                    sortOrder: selectedTab === "SKILLS" ? skillsSortDir :
                      selectedTab === "CERTIFICATIONS" ? certificationsSortDir :
                        selectedTab === "POSITIONS" ? positionsSortDir :
                          selectedTab === "DIVISIONS" ? divisionsSortDir :
                            selectedTab === "OFFICES" ? officesSortDir :
                              appNotesSortDir,
                    sortOptions: SETTINGS_SORT_OPTIONS,
                    onSortByChange: (value) => {
                      if (selectedTab === "SKILLS") setSkillsSortKey(value);
                      else if (selectedTab === "CERTIFICATIONS") setCertificationsSortKey(value);
                      else if (selectedTab === "POSITIONS") setPositionsSortKey(value);
                      else if (selectedTab === "DIVISIONS") setDivisionsSortKey(value);
                      else if (selectedTab === "OFFICES") setOfficesSortKey(value);
                      else if (selectedTab === "APP NOTES") setAppNotesSortKey(value);
                      setCurrentPage(1);
                    },
                    onSortOrderToggle: () => {
                      const setDir = selectedTab === "SKILLS" ? setSkillsSortDir :
                        selectedTab === "CERTIFICATIONS" ? setCertificationsSortDir :
                          selectedTab === "POSITIONS" ? setPositionsSortDir :
                            selectedTab === "DIVISIONS" ? setDivisionsSortDir :
                              selectedTab === "OFFICES" ? setOfficesSortDir :
                                setAppNotesSortDir;
                      setDir((prev) => (prev === "asc" ? "desc" : "asc"));
                      setCurrentPage(1);
                    },
                  }}
                  actions={
                    <div className="flex gap-2">
                      {selectedTab === "SKILLS" && (
                        <button
                          type="button"
                          onClick={() => setShowCreateSkill(true)}
                          className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                        >
                          CREATE
                        </button>
                      )}
                      {selectedTab === "CERTIFICATIONS" && (
                        <button
                          type="button"
                          onClick={handleCreateCertification}
                          className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                        >
                          CREATE
                        </button>
                      )}
                      {selectedTab === "POSITIONS" && (
                        <button
                          type="button"
                          onClick={handleCreatePosition}
                          className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                        >
                          CREATE
                        </button>
                      )}
                      {selectedTab === "DIVISIONS" && (
                        <button
                          type="button"
                          onClick={handleCreateDivision}
                          className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                        >
                          CREATE
                        </button>
                      )}
                      {selectedTab === "OFFICES" && (
                        <button
                          type="button"
                          onClick={() => setShowCreateOffice(true)}
                          className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                        >
                          CREATE
                        </button>
                      )}
                      {selectedTab === "APP NOTES" && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingAppNote(null);
                            setShowCreateAppNote(true);
                          }}
                          className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                        >
                          CREATE
                        </button>
                      )}
                    </div>
                  }
                  chipBar={
                    (() => {
                      const chips = selectedTab === "SKILLS" ? buildAdminSettingsSkillsFilterChips({ divisionFilter, showDeleted, sortDir: skillsSortDir, divisions }) :
                        selectedTab === "CERTIFICATIONS" ? buildAdminSettingsCertificationsFilterChips({ divisionFilter, showDeleted, sortDir: certificationsSortDir, divisions }) :
                          selectedTab === "POSITIONS" ? buildAdminSettingsPositionsFilterChips({ divisionFilter, showDeleted, sortDir: positionsSortDir, divisions }) :
                            selectedTab === "DIVISIONS" ? buildAdminSettingsDivisionsFilterChips({ showDeleted, sortDir: divisionsSortDir }) :
                              selectedTab === "OFFICES" ? buildAdminSettingsOfficesFilterChips({ showDeleted, sortDir: officesSortDir }) :
                                selectedTab === "APP NOTES" ? buildAdminSettingsAppNotesFilterChips({ showDeleted, sortDir: appNotesSortDir, selectedSigninStatuses }) : [];
                      return chips.length > 0 ? (
                        <AtlasFilterChipBar
                          chips={chips}
                          onChipClick={(chip) => {
                            if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                            else filterPanel.open();
                          }}
                          onRemove={removeFilter}
                          onClearAll={() => {
                            setSearchTerm("");
                            setDivisionFilter([]);
                            setShowDeleted(false);
                            setSelectedSigninStatuses([]);
                          }}
                        />
                      ) : null;
                    })()
                  }
                />
              )}

              {!isSigninSettingsTab && (
                <AtlasFilterPanel
                  isOpen={filterPanel.isOpen}
                  onClose={filterPanel.close}
                  onApply={() => {
                    setCurrentPage(1);
                    filterPanel.close();
                  }}
                  onClear={() => {
                    setSearchTerm("");
                    setDivisionFilter([]);
                    setShowDeleted(false);
                    setSelectedSigninStatuses([]);
                  }}
                >
                  {selectedTab === "SKILLS" && (
                    <AdminSettingsSkillsFilterFields
                      focusedFieldId={filterPanel.focusedFieldId}
                      onFocusClear={filterPanel.clearFocusedField}
                      divisionFilter={divisionFilter}
                      setDivisionFilter={setDivisionFilter}
                      showDeleted={showDeleted}
                      setShowDeleted={setShowDeleted}
                      sortKey={skillsSortKey}
                      setSortKey={setSkillsSortKey}
                      sortDir={skillsSortDir}
                      setSortDir={setSkillsSortDir}
                      divisions={divisions}
                    />
                  )}
                  {selectedTab === "CERTIFICATIONS" && (
                    <AdminSettingsCertificationsFilterFields
                      focusedFieldId={filterPanel.focusedFieldId}
                      onFocusClear={filterPanel.clearFocusedField}
                      divisionFilter={divisionFilter}
                      setDivisionFilter={setDivisionFilter}
                      showDeleted={showDeleted}
                      setShowDeleted={setShowDeleted}
                      sortKey={certificationsSortKey}
                      setSortKey={setCertificationsSortKey}
                      sortDir={certificationsSortDir}
                      setSortDir={setCertificationsSortDir}
                      divisions={divisions}
                    />
                  )}
                  {selectedTab === "POSITIONS" && (
                    <AdminSettingsPositionsFilterFields
                      focusedFieldId={filterPanel.focusedFieldId}
                      onFocusClear={filterPanel.clearFocusedField}
                      divisionFilter={divisionFilter}
                      setDivisionFilter={setDivisionFilter}
                      showDeleted={showDeleted}
                      setShowDeleted={setShowDeleted}
                      sortKey={positionsSortKey}
                      setSortKey={setPositionsSortKey}
                      sortDir={positionsSortDir}
                      setSortDir={setPositionsSortDir}
                      divisions={divisions}
                    />
                  )}
                  {selectedTab === "DIVISIONS" && (
                    <AdminSettingsDivisionsFilterFields
                      focusedFieldId={filterPanel.focusedFieldId}
                      onFocusClear={filterPanel.clearFocusedField}
                      showDeleted={showDeleted}
                      setShowDeleted={setShowDeleted}
                      sortKey={divisionsSortKey}
                      setSortKey={setDivisionsSortKey}
                      sortDir={divisionsSortDir}
                      setSortDir={setDivisionsSortDir}
                    />
                  )}
                  {selectedTab === "OFFICES" && (
                    <AdminSettingsOfficesFilterFields
                      focusedFieldId={filterPanel.focusedFieldId}
                      onFocusClear={filterPanel.clearFocusedField}
                      showDeleted={showDeleted}
                      setShowDeleted={setShowDeleted}
                      sortKey={officesSortKey}
                      setSortKey={setOfficesSortKey}
                      sortDir={officesSortDir}
                      setSortDir={setOfficesSortDir}
                    />
                  )}
                  {selectedTab === "APP NOTES" && (
                    <AdminSettingsAppNotesFilterFields
                      focusedFieldId={filterPanel.focusedFieldId}
                      onFocusClear={filterPanel.clearFocusedField}
                      selectedSigninStatuses={selectedSigninStatuses}
                      setSelectedSigninStatuses={setSelectedSigninStatuses}
                      showDeleted={showDeleted}
                      setShowDeleted={setShowDeleted}
                      sortKey={appNotesSortKey}
                      setSortKey={setAppNotesSortKey}
                      sortDir={appNotesSortDir}
                      setSortDir={setAppNotesSortDir}
                    />
                  )}
                </AtlasFilterPanel>
              )}
              {isSigninSettingsTab && (
                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm mb-6">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground mb-2">System Settings</h2>
                      <p className="text-sm text-muted-foreground max-w-2xl">
                        Control whether login 2-step verification is enforced system-wide. When disabled,
                        login OTP is bypassed for everyone even if individual accounts have Force 2FA enabled.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={loadSigninSettings}
                      disabled={signinSettingsLoading || signinSettingsSaving}
                      className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                    >
                      {signinSettingsLoading ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>

                  <div className="mt-6 rounded-lg border border-gray-200 p-5">
                    <div className="flex items-center justify-between gap-6">
                      <div>
                        <div className="text-sm font-medium text-foreground">Enable sign-in 2-step verification</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Master switch for the whole system. `signin2faEnabled = 0` bypasses login OTP for everyone.
                        </div>
                      </div>
                      <label className="inline-flex items-center gap-3 text-sm font-medium text-foreground cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#DC1F00]"
                          checked={signin2faEnabled}
                          disabled={adminReadOnly || signinSettingsLoading || signinSettingsSaving}
                          onChange={(e) => saveSigninSettings(e.target.checked)}
                        />
                        <span>{signin2faEnabled ? "Enabled" : "Disabled"}</span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-6 rounded-lg border border-gray-200 p-5">
                    <div className="flex flex-col gap-4">
                      <div>
                        <div className="text-sm font-medium text-foreground">Display timezone</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          System-wide timezone for dates and times shown in the app. Default is US Eastern
                          (`America/New_York`, EDT/EST).
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <TimezoneSelect
                          className="flex-1"
                          value={selectedTimezone}
                          disabled={adminReadOnly || signinSettingsLoading || timezoneSaving}
                          onChange={setSelectedTimezone}
                        />
                        <button
                          type="button"
                          onClick={saveTimezoneSettings}
                          disabled={adminReadOnly || signinSettingsLoading || timezoneSaving}
                          className="rounded-lg bg-[#DC1F00] px-4 py-2 text-sm font-medium text-white hover:bg-[#b81900] disabled:opacity-50"
                        >
                          {timezoneSaving ? "Saving..." : "Save timezone"}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 rounded-lg border border-gray-200 p-5">
                    <div className="flex flex-col gap-4">
                      <div>
                        <div className="text-sm font-medium text-foreground">HST (HTS) Tax Rate (%)</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          System-wide tax rate percentage used for billing and invoice calculations. Default is 13%.
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          disabled={adminReadOnly || signinSettingsLoading || hstRateSaving}
                          value={hstRate}
                          onChange={(e) => setHstRate(e.target.value)}
                          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#DC1F00] bg-white text-foreground"
                        />
                        <button
                          type="button"
                          onClick={saveHstSettings}
                          disabled={adminReadOnly || signinSettingsLoading || hstRateSaving}
                          className="rounded-lg bg-[#DC1F00] px-4 py-2 text-sm font-medium text-white hover:bg-[#b81900] disabled:opacity-50"
                        >
                          {hstRateSaving ? "Saving..." : "Save HST rate"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Table for SKILLS */}
              {selectedTab === "SKILLS" && (
                <div className="overflow-x-auto table-force-rows no-mobile-table-stack">
                  <table className="min-w-full border rounded-lg overflow-hidden shadow-lg">
                    <thead className="bg-[#dc1f0080] text-white">
                      <tr>
                        <th className="px-6 py-3 text-sm font-medium text-left w-1/4">
                          <span>Name</span>
                          <SortArrow
                            colKey="name"
                            activeKey={skillsSortKey}
                            activeDir={skillsSortDir}
                            onChange={(k, d) => {
                              setSkillsSortKey(k);
                              setSkillsSortDir(d);
                            }}
                          />
                        </th>
                        <th className="px-4 py-3 text-sm font-medium text-left w-1/2">
                          <span>Divisions</span>
                        </th>
                        <th className="px-4 py-3 text-sm font-medium w-[3%]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {skillsLoading && (
                        <tr>
                          <td className="pl-6 py-3 text-sm" colSpan={3}>
                            Loading…
                          </td>
                        </tr>
                      )}
                      {skillsError && !skillsLoading && (
                        <tr>
                          <td className="pl-6 py-3 text-sm text-red-600" colSpan={3}>
                            {skillsError}
                          </td>
                        </tr>
                      )}
                      {!skillsLoading &&
                        !skillsError &&
                        sortedAndFilteredSkills.length === 0 && (
                          <tr>
                            <td className="pl-6 py-3 text-sm" colSpan={3}>
                              No skills found
                            </td>
                          </tr>
                        )}
                      {!skillsLoading && !skillsError && paginatedSkills.map((s, index) => (
                        <tr key={s.id} className={`text-sm border-b border-gray-200 transition hover:bg-orange-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <td className="px-6 py-3">
                            {showDeleted ? (
                              <span className="text-gray-500 font-medium cursor-default">
                                {s.name}
                              </span>
                            ) : (
                              <button
                                className="text-[#DC1F00] font-medium cursor-pointer hover:opacity-80 bg-transparent p-0 border-none"
                                type="button"
                                onClick={() => { setEditingSkill(s); setShowEditSkill(true); }}
                                tabIndex={0}
                                aria-label={`Edit skill: ${s.name}`}
                              >
                                {s.name}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3">{s.division_name || ''}</td>
                          <td className="px-4 py-3">
                            <ActionDropdown
                              actions={[
                                showDeleted ? {
                                  label: "Restore",
                                  onClick: async () => {
                                    if (!(await showConfirm('Are you sure you want to restore this skill?'))) return;
                                    try {
                                      const res = await fetch(`${BASE_URL}/api/admin/skills/${s.id}/restore`, {
                                        method: 'PUT',
                                        credentials: 'include'
                                      });
                                      const data = await res.json();
                                      if (!res.ok) throw new Error(data.message || 'Failed to restore skill');

                                      // Remove from local state
                                      setSkills(prev => prev.filter(skill => skill.id !== s.id));
                                      setSkillsTotal(prev => prev - 1);
                                      showSuccess('Skill restored successfully');
                                    } catch (e) {
                                      showError('Failed to restore skill: ' + e.message);
                                    }
                                  }
                                } : {
                                  label: "Delete",
                                  onClick: async () => {
                                    if (!(await showConfirm('Are you sure you want to delete this skill?'))) return;

                                    try {
                                      const res = await fetch(`${BASE_URL}/api/admin/skills/${s.id}`, {
                                        method: 'DELETE',
                                        credentials: 'include'
                                      });
                                      const data = await res.json();

                                      if (!res.ok) throw new Error(data.message || 'Failed to delete skill');

                                      // Remove from local state
                                      setSkills(prev => prev.filter(skill => skill.id !== s.id));
                                      setSkillsTotal(prev => prev - 1);
                                    } catch (e) {
                                      showError('Failed to delete skill: ' + e.message);
                                    }
                                  }
                                }]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Table for CERTIFICATIONS */}
              {selectedTab === "CERTIFICATIONS" && (
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow no-mobile-table-stack">
                  <table className="min-w-full">
                    <thead className="bg-[#dc1f0080] text-white">
                      <tr className="text-left select-none">
                        <th className="text-sm font-medium px-4 py-2 w-[36%]">
                          <span>Name</span>
                          <SortArrow
                            colKey="name"
                            activeKey={certificationsSortKey}
                            activeDir={certificationsSortDir}
                            onChange={(k, d) => {
                              setCertificationsSortKey(k);
                              setCertificationsSortDir(d);
                            }}
                          />
                        </th>
                        <th className="text-sm font-medium px-4 py-2 w-[60%]">
                          Skills and Divisions
                        </th>
                        <th className="text-sm font-medium px-3 py-2 w-[4%]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {certificationsLoading && (
                        <tr>
                          <td className="px-4 py-2 text-sm" colSpan={3}>
                            Loading…
                          </td>
                        </tr>
                      )}
                      {certificationsError && !certificationsLoading && (
                        <tr>
                          <td
                            className="px-4 py-2 text-sm text-red-600"
                            colSpan={3}
                          >
                            {certificationsError}
                          </td>
                        </tr>
                      )}
                      {!certificationsLoading &&
                        !certificationsError &&
                        certifications.length === 0 && (
                          <tr>
                            <td className="px-4 py-2 text-sm" colSpan={3}>
                              No certifications found
                            </td>
                          </tr>
                        )}
                      {!certificationsLoading &&
                        !certificationsError &&
                        certifications.map((cert, index) => (
                          <tr
                            key={cert.id}
                            className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }`}
                          >
                            <td
                              onClick={() => !showDeleted && handleEditCertification(cert)}
                              className={`px-4 py-2.5 font-medium whitespace-normal break-words ${showDeleted ? 'text-gray-500 cursor-default' : 'text-[#DC1F00] cursor-pointer'}`}
                            >
                              {cert.certification_name ||
                                cert.name ||
                                cert.certificationName ||
                                cert.certName ||
                                ""}
                            </td>
                            <td className="px-4 py-2.5 text-sm whitespace-normal break-words">
                              {Array.isArray(cert.skills) && cert.skills.length > 0
                                ? cert.skills.map((sk) => (
                                  <div
                                    key={sk.skillId || sk.skill_id || sk.id}
                                    className="mb-1"
                                  >
                                    <strong>
                                      {sk.skillName || sk.skill_name || sk.name}
                                    </strong>
                                    {sk.divisions && sk.divisions.length > 0 ? (
                                      <span>: {sk.divisions.join(", ")}</span>
                                    ) : null}
                                  </div>
                                ))
                                : (cert.skills &&
                                  typeof cert.skills === "string" &&
                                  cert.skills) ||
                                ""}
                            </td>
                            <td className="py-2.5 pr-2">
                              <ActionDropdown
                                actions={showDeleted ? [
                                  {
                                    label: "Restore",
                                    onClick: async () => {
                                      if (!(await showConfirm('Are you sure you want to restore this certification?'))) return;
                                      try {
                                        const res = await fetch(`${BASE_URL}/api/admin/certifications/${cert.id}/restore`, {
                                          method: 'PUT',
                                          credentials: 'include'
                                        });
                                        const data = await res.json();
                                        if (!res.ok) throw new Error(data.message || 'Failed to restore certification');

                                        // Remove from local state
                                        setCertifications(prev => prev.filter(c => c.id !== cert.id));
                                        setCertificationsTotal(prev => prev - 1);
                                        showSuccess('Certification restored successfully');
                                      } catch (e) {
                                        showError('Failed to restore certification: ' + e.message);
                                      }
                                    }
                                  }
                                ] : [
                                  // { label: "Edit", onClick: () => handleEditCertification(cert) },
                                  {
                                    label: "Delete",
                                    onClick: () =>
                                      handleDeleteCertification(cert.id),
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Table for POSITIONS */}
              {selectedTab === "POSITIONS" && (
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow no-mobile-table-stack">
                  <table className="min-w-full">
                    <thead className="bg-[#dc1f0080] text-white">
                      <tr className="text-left select-none">
                        <th className="text-sm font-medium px-4 py-2 w-1/5">
                          <span>Name</span>
                          <SortArrow
                            colKey="name"
                            activeKey={positionsSortKey}
                            activeDir={positionsSortDir}
                            onChange={(k, d) => {
                              setPositionsSortKey(k);
                              setPositionsSortDir(d);
                            }}
                          />
                        </th>
                        <th className="text-sm font-medium px-4 py-2 w-1/4">
                          Divisions
                        </th>
                        <th className="text-sm font-medium px-3 py-2 w-[3%]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {positionsLoading && (
                        <tr>
                          <td className="px-4 py-2 text-sm" colSpan={3}>
                            Loading…
                          </td>
                        </tr>
                      )}
                      {positionsError && !positionsLoading && (
                        <tr>
                          <td
                            className="px-4 py-2 text-sm text-red-600"
                            colSpan={3}
                          >
                            {positionsError}
                          </td>
                        </tr>
                      )}
                      {!positionsLoading &&
                        !positionsError &&
                        positions.length === 0 && (
                          <tr>
                            <td className="px-4 py-2 text-sm" colSpan={3}>
                              No positions found
                            </td>
                          </tr>
                        )}
                      {!positionsLoading &&
                        !positionsError &&
                        positions.map((position, index) => (
                          <tr
                            key={position.id}
                            className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }`}
                          >
                            <td
                              className={`px-4 py-2.5 font-medium whitespace-normal break-words ${showDeleted ? 'text-gray-500 cursor-default' : 'text-[#DC1F00] cursor-pointer'}`}
                              onClick={() => !showDeleted && handleEditPosition(position)}
                            >
                              {position.position_name}
                            </td>
                            <td className="px-4 py-2.5 text-sm whitespace-normal break-words">
                              {position.divisions || ""}
                            </td>
                            <td className="py-2.5 pr-2">
                              <ActionDropdown
                                actions={showDeleted ? [
                                  {
                                    label: "Restore",
                                    onClick: async () => {
                                      if (!(await showConfirm('Are you sure you want to restore this position?'))) return;
                                      try {
                                        const res = await fetch(`${BASE_URL}/api/admin/positions/${position.id}/restore`, {
                                          method: 'PUT',
                                          credentials: 'include'
                                        });
                                        const data = await res.json();
                                        if (!res.ok) throw new Error(data.message || 'Failed to restore position');

                                        // Remove from local state
                                        setPositions(prev => prev.filter(p => p.id !== position.id));
                                        setPositionsTotal(prev => prev - 1);
                                        showSuccess('Position restored successfully');
                                      } catch (e) {
                                        showError('Failed to restore position: ' + e.message);
                                      }
                                    }
                                  }
                                ] : [
                                  {
                                    label: "Delete",
                                    onClick: () =>
                                      handleDeletePosition(position.id),
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Table for DIVISIONS */}
              {selectedTab === "DIVISIONS" && (
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow no-mobile-table-stack">
                  <table className="min-w-full">
                    <thead className="bg-[#dc1f0080] text-white">
                      <tr className="text-left select-none">
                        <th className="text-sm font-medium px-4 py-2 w-1/5">
                          <span>Division Name</span>
                          <SortArrow
                            colKey="name"
                            activeKey={divisionsSortKey}
                            activeDir={divisionsSortDir}
                            onChange={(k, d) => {
                              setDivisionsSortKey(k);
                              setDivisionsSortDir(d);
                            }}
                          />
                        </th>
                        {/* for later use */}
                        {/* <th className="text-sm font-medium px-4 py-2 w-1/4">
                      <span>Client(s)</span>
                      <SortArrow
                        colKey="clients"
                        activeKey={divisionsSortKey}
                        activeDir={divisionsSortDir}
                        onChange={(k, d) => {
                          setDivisionsSortKey(k);
                          setDivisionsSortDir(d);
                        }}
                      />
                    </th> */}
                        <th className="text-sm font-medium px-4 py-2 w-[15%]">
                          <span>Staff Availability Confirmation</span>
                        </th>
                        {/* for later use */}
                        {/* <th className="text-sm font-medium px-4 py-2 w-[12%]">
                      <span>Status</span>
                      <SortArrow
                        colKey="state"
                        activeKey={divisionsSortKey}
                        activeDir={divisionsSortDir}
                        onChange={(k, d) => {
                          setDivisionsSortKey(k);
                          setDivisionsSortDir(d);
                        }}
                      />
                    </th> */}
                        <th className="text-sm font-medium px-3 py-2 w-[1%]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {divisionsLoading && (
                        <tr>
                          <td className="px-4 py-2 text-sm" colSpan={5}>
                            Loading…
                          </td>
                        </tr>
                      )}
                      {divisionsError && !divisionsLoading && (
                        <tr>
                          <td
                            className="px-4 py-2 text-sm text-red-600"
                            colSpan={5}
                          >
                            {divisionsError}
                          </td>
                        </tr>
                      )}
                      {!divisionsLoading &&
                        !divisionsError &&
                        divisionsList.length === 0 && (
                          <tr>
                            <td className="px-4 py-2 text-sm" colSpan={5}>
                              No divisions found
                            </td>
                          </tr>
                        )}
                      {!divisionsLoading &&
                        !divisionsError &&
                        divisionsList.map((division, index) => (
                          <tr
                            key={division.id}
                            className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }`}
                          >
                            <td
                              onClick={() => !showDeleted && handleEditDivision(division)}
                              className={`px-4 py-2.5 font-medium whitespace-normal break-words ${showDeleted ? 'text-gray-500 cursor-default' : 'text-[#DC1F00] cursor-pointer'}`}
                            >
                              {division.division_name}
                            </td>
                            {/* <td className="px-4 py-2.5 text-sm whitespace-normal break-words">{division.clients || ''}</td> */}
                            <td className="px-4 py-2.5">
                              <span
                                className={`px-2 py-1 rounded text-xs ${division.availability_mode === "automatic"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-orange-100 text-orange-800"
                                  }`}
                              >
                                {division.availability_mode === "automatic" ? "automatic" : "manual"}
                              </span>
                            </td>
                            {/* for later use */}
                            {/* <td className="px-4 py-2.5">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            division.state === "active"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {division.state || "inactive"}
                        </span>
                      </td> */}
                            <td className="py-2.5 pr-2">
                              <ActionDropdown
                                actions={showDeleted ? [
                                  {
                                    label: "Restore",
                                    onClick: async () => {
                                      if (!(await showConfirm('Are you sure you want to restore this division?'))) return;
                                      try {
                                        const res = await fetch(`${BASE_URL}/api/admin/divisions/${division.id}/restore`, {
                                          method: 'PUT',
                                          credentials: 'include'
                                        });
                                        const data = await res.json();
                                        if (!res.ok) throw new Error(data.message || 'Failed to restore division');

                                        // Remove from local state
                                        setDivisionsList(prev => prev.filter(d => d.id !== division.id));
                                        setDivisionsTotal(prev => prev - 1);
                                        showSuccess('Division restored successfully');
                                      } catch (e) {
                                        showError('Failed to restore division: ' + e.message);
                                      }
                                    }
                                  }
                                ] : [
                                  // { label: "Edit", onClick: () => handleEditDivision(division) },
                                  {
                                    label: "Delete",
                                    onClick: () =>
                                      handleDeleteDivision(division.id),
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Table for OFFICES */}
              {selectedTab === "OFFICES" && (
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow table-force-rows no-mobile-table-stack">
                  <table className="min-w-full">
                    <thead className="bg-[#dc1f0080] text-white">
                      <tr className="text-left select-none">
                        <th className="text-sm font-medium px-4 py-2 w-1/4">
                          <span>Name</span>
                          <SortArrow
                            colKey="name"
                            activeKey={officesSortKey}
                            activeDir={officesSortDir}
                            onChange={(k, d) => {
                              setOfficesSortKey(k);
                              setOfficesSortDir(d);
                            }}
                          />
                        </th>
                        <th className="text-sm font-medium px-4 py-2 w-1/2">
                          <span>Address</span>
                        </th>
                        <th className="text-sm font-medium px-3 py-2 w-[3%]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {officesLoading && (
                        <tr>
                          <td className="px-4 py-2 text-sm" colSpan={3}>
                            Loading…
                          </td>
                        </tr>
                      )}
                      {officesError && !officesLoading && (
                        <tr>
                          <td
                            className="px-4 py-2 text-sm text-red-600"
                            colSpan={3}
                          >
                            {officesError}
                          </td>
                        </tr>
                      )}
                      {!officesLoading && !officesError && offices.length === 0 && (
                        <tr>
                          <td className="px-4 py-2 text-sm" colSpan={3}>
                            No offices found
                          </td>
                        </tr>
                      )}
                      {!officesLoading &&
                        !officesError &&
                        offices.map((office, index) => (
                          <tr
                            key={office.id}
                            className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }`}
                          >
                            <td
                              onClick={() => {
                                if (showDeleted) return;
                                setEditingOffice(office);
                                setShowEditOffice(true);
                              }}
                              className={`px-4 py-2.5 font-medium ${showDeleted ? 'text-gray-500 cursor-default' : 'text-[#DC1F00] cursor-pointer'}`}
                            >
                              {office.name}
                            </td>
                            <td className="px-4 py-2.5 text-sm">
                              {office.address}
                            </td>
                            <td className="py-2.5 pr-2">
                              <ActionDropdown
                                actions={showDeleted ? [
                                  {
                                    label: "Restore",
                                    onClick: async () => {
                                      if (!(await showConfirm('Are you sure you want to restore this office?'))) return;
                                      try {
                                        const res = await fetch(`${BASE_URL}/api/admin/offices/${office.id}/restore`, {
                                          method: 'PUT',
                                          credentials: 'include'
                                        });
                                        const data = await res.json();
                                        if (!res.ok) throw new Error(data.message || 'Failed to restore office');

                                        // Remove from local state
                                        setOffices(prev => prev.filter(o => o.id !== office.id));
                                        setOfficesTotal(prev => prev - 1);
                                        showSuccess('Office restored successfully');
                                      } catch (e) {
                                        showError('Failed to restore office: ' + e.message);
                                      }
                                    }
                                  }
                                ] : [
                                  // { label: "Edit", onClick: () => {
                                  //   setEditingOffice(office);
                                  //   setShowEditOffice(true);
                                  // }},
                                  {
                                    label: "Delete",
                                    onClick: async () => {
                                      if (
                                        !(await showConfirm(
                                          "Are you sure you want to delete this office?"
                                        ))
                                      )
                                        return;
                                      try {
                                        const res = await fetch(
                                          `${BASE_URL}/api/admin/offices/${office.id}`,
                                          {
                                            method: "DELETE",
                                            credentials: "include",
                                          }
                                        );
                                        const data = await res.json();
                                        if (!res.ok)
                                          throw new Error(
                                            data.message ||
                                            "Failed to delete office"
                                          );
                                        // Reload offices (preserve filters)
                                        const q = encodeURIComponent(
                                          searchTerm || ""
                                        );
                                        const reloadRes = await fetch(
                                          `${BASE_URL}/api/admin/offices?page=${currentPage}&limit=${pageSize}&search=${q}` +
                                          `&showDeleted=${showDeleted}` +
                                          `&sort=${encodeURIComponent(
                                            officesSortKey
                                          )}` +
                                          `&dir=${encodeURIComponent(
                                            officesSortDir
                                          )}`,
                                          { credentials: "include" }
                                        );
                                        const reloadData = await reloadRes.json();
                                        setOffices(reloadData.data || []);
                                        setOfficesTotal(reloadData.total || 0);
                                      } catch (e) {
                                        showError(
                                          "Failed to delete office: " + e.message
                                        );
                                      }
                                    },
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Table for APP NOTES */}
              {selectedTab === "APP NOTES" && (
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow no-mobile-table-stack">
                  <table className="min-w-full">
                    <thead className="bg-[#dc1f0080] text-white">
                      <tr className="text-left select-none">
                        <th className="text-sm font-medium px-4 py-2 w-1/4">
                          <span>Name</span>
                          <SortArrow
                            colKey="name"
                            activeKey={appNotesSortKey}
                            activeDir={appNotesSortDir}
                            onChange={(k, d) => {
                              setAppNotesSortKey(k);
                              setAppNotesSortDir(d);
                            }}
                          />
                        </th>
                        <th className="text-sm font-medium px-4 py-2 w-1/4">
                          <span>App Pages</span>
                        </th>
                        <th className="text-sm font-medium px-4 py-2 w-1/6">
                          <span>Usage</span>
                          <SortArrow
                            colKey="usage"
                            activeKey={appNotesSortKey}
                            activeDir={appNotesSortDir}
                            onChange={(k, d) => {
                              setAppNotesSortKey(k);
                              setAppNotesSortDir(d);
                            }}
                          />
                        </th>
                        {/* <th className="text-sm font-medium px-4 py-2 w-[8%]">
                      <span>State</span>
                      <SortArrow
                        colKey="state"
                        activeKey={appNotesSortKey}
                        activeDir={appNotesSortDir}
                        onChange={(k, d) => {
                          setAppNotesSortKey(k);
                          setAppNotesSortDir(d);
                        }}
                      />
                    </th> */}
                        <th className="text-sm font-medium px-3 py-2 w-[3%]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {appNotesLoading && (
                        <tr>
                          <td className="px-4 py-2 text-sm" colSpan={5}>
                            Loading…
                          </td>
                        </tr>
                      )}
                      {appNotesError && !appNotesLoading && (
                        <tr>
                          <td
                            className="px-4 py-2 text-sm text-red-600"
                            colSpan={5}
                          >
                            {appNotesError}
                          </td>
                        </tr>
                      )}
                      {!appNotesLoading &&
                        !appNotesError &&
                        appNotes.length === 0 && (
                          <tr>
                            <td className="px-4 py-2 text-sm" colSpan={5}>
                              No app notes found
                            </td>
                          </tr>
                        )}
                      {!appNotesLoading &&
                        !appNotesError &&
                        appNotes.map((note, index) => (
                          <tr
                            key={note.id}
                            className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }`}
                          >
                            <td
                              className={`px-4 py-2.5 font-medium whitespace-normal break-words ${showDeleted ? 'text-gray-500 cursor-default' : 'text-[#DC1F00] cursor-pointer'}`}
                              onClick={() => {
                                if (showDeleted) return;
                                setEditingAppNote(note);
                                setShowEditAppNote(true);
                              }}
                            >
                              {note.name}
                            </td>
                            <td className="px-4 py-2.5 text-sm whitespace-normal break-words">
                              {note.normalized_types || note.app_pages}
                            </td>
                            <td className="px-4 py-2.5 text-sm">
                              {note.usage_count}
                            </td>
                            {/* <td className="px-4 py-2.5">
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              note.state === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {note.state || "inactive"}
                          </span>
                        </td> */}
                            <td className="py-2.5 pr-2">
                              <ActionDropdown
                                actions={showDeleted ? [
                                  {
                                    label: "Restore",
                                    onClick: async () => {
                                      if (!(await showConfirm('Are you sure you want to restore this app note?'))) return;
                                      try {
                                        const res = await fetch(`${BASE_URL}/api/admin/app-notes/${note.id}/restore`, {
                                          method: 'PUT',
                                          credentials: 'include'
                                        });
                                        const data = await res.json();
                                        if (!res.ok) throw new Error(data.message || 'Failed to restore app note');

                                        // Remove from local state
                                        setAppNotes(prev => prev.filter(n => n.id !== note.id));
                                        setAppNotesTotal(prev => prev - 1);
                                        showSuccess('App note restored successfully');
                                      } catch (e) {
                                        showError('Failed to restore app note: ' + e.message);
                                      }
                                    }
                                  }
                                ] : [
                                  // {
                                  //   label: "Edit",
                                  //   onClick: () => {
                                  //     setEditingAppNote(note);
                                  //     setShowEditAppNote(true);
                                  //   },
                                  // },
                                  {
                                    label: "Delete",
                                    onClick: async () => {
                                      if (
                                        !(await showConfirm(
                                          "Are you sure you want to delete this app note?"
                                        ))
                                      )
                                        return;
                                      try {
                                        const res = await fetch(
                                          `${BASE_URL}/api/admin/app-notes/${note.app_note_id}`,
                                          {
                                            method: "DELETE",
                                            credentials: "include",
                                          }
                                        );
                                        const data = await res.json();
                                        if (!res.ok)
                                          throw new Error(
                                            data.message ||
                                            "Failed to delete app note"
                                          );

                                        // Reload app notes (preserve filters)
                                        const q = encodeURIComponent(
                                          searchTerm || ""
                                        );
                                        const status = encodeURIComponent(
                                          selectedSigninStatuses.join(",") || ""
                                        );
                                        const reloadRes = await fetch(
                                          `${BASE_URL}/api/admin/app-notes?page=${currentPage}&limit=${pageSize}&search=${q}&status=${status}` +
                                          `&showDeleted=${showDeleted}` +
                                          `&sort=${encodeURIComponent(
                                            appNotesSortKey
                                          )}` +
                                          `&dir=${encodeURIComponent(
                                            appNotesSortDir
                                          )}`,
                                          { credentials: "include" }
                                        );
                                        const reloadData = await reloadRes.json();
                                        setAppNotes(
                                          mapNotes(reloadData.data || [])
                                        );
                                        setAppNotesTotal(reloadData.total || 0);
                                      } catch (e) {
                                        showError(
                                          "Failed to delete app note: " + e.message
                                        );
                                      }
                                    },
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Pagination controller */}
              {(selectedTab === "SKILLS" ||
                selectedTab === "CERTIFICATIONS" ||
                selectedTab === "POSITIONS" ||
                selectedTab === "DIVISIONS" ||
                selectedTab === "OFFICES" ||
                selectedTab === "APP NOTES") && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-auto mt-4 mb-6 text-sm text-muted-foreground">
                    <span>
                      {/* Summary moved to header */}
                    </span>
                    <div className="flex items-center gap-1 sm:gap-2 justify-between sm:justify-end w-full sm:w-auto">
                      <button
                        className="px-3 py-2 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100 disabled:opacity-50"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Previous
                      </button>

                      <div className="hidden md:flex items-center gap-1 sm:gap-2">
                        {(() => {
                          const pages = [];
                          const maxVisible = 5;
                          const start = Math.max(
                            1,
                            currentPage - Math.floor(maxVisible / 2)
                          );
                          const end = Math.min(totalPages, start + maxVisible - 1);

                          if (start > 1) {
                            pages.push(1);
                            if (start > 2) pages.push("...");
                          }

                          for (let i = start; i <= end; i++) {
                            pages.push(i);
                          }

                          if (end < totalPages) {
                            if (end < totalPages - 1) pages.push("...");
                            pages.push(totalPages);
                          }

                          return pages.map((page, idx) =>
                            page === "..." ? (
                              <span key={idx} className="px-3 py-2">
                                ...
                              </span>
                            ) : (
                              <button
                                key={idx}
                                className={`px-3 py-2 border border-[#DC1F0033] rounded-md ${currentPage === page
                                  ? "bg-[#DC1F00] text-white"
                                  : "hover:bg-gray-100"
                                  }`}
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </button>
                            )
                          );
                        })()}
                      </div>

                      <span className="md:hidden px-2 text-xs">
                        Page {currentPage} of {totalPages}
                      </span>

                      <button
                        className="px-3 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-md disabled:opacity-50"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
        {/* Create/Edit Certification Modal */}
        {
          (showCreateCertification || showEditCertification) && (
            <CertificationModal
              certification={editingCertification}
              divisions={divisions}
              skills={availableSkills}
              onClose={() => {
                setShowCreateCertification(false);
                setShowEditCertification(false);
                setEditingCertification(null);
              }}
              onSave={() => {
                // Reload handled by useEffect on modal close
              }}
            />
          )
        }
        {/* Create/Edit Position Modal */}
        {
          (showCreatePosition || showEditPosition) && (
            <PositionModal
              position={editingPosition}
              divisions={divisions}
              skills={availableSkills}
              certifications={availableCertifications}
              onClose={() => {
                setShowCreatePosition(false);
                setShowEditPosition(false);
                setEditingPosition(null);
              }}
              onSave={() => {
                // Reload handled by useEffect on modal close
              }}
            />
          )
        }
        {/* Create/Edit Office Modal */}
        {
          (showCreateOffice || showEditOffice) && (
            <OfficeModal
              office={editingOffice}
              onClose={() => {
                setShowCreateOffice(false);
                setShowEditOffice(false);
                setEditingOffice(null);
              }}
              onSave={async () => {
                // Reload offices
                const q = encodeURIComponent(searchTerm || "");
                const res = await fetch(
                  `${BASE_URL}/api/admin/offices?page=${currentPage}&limit=${pageSize}&search=${q}&sort=${encodeURIComponent(
                    officesSortKey
                  )}&dir=${encodeURIComponent(officesSortDir)}`,
                  { credentials: "include" }
                );
                const data = await res.json();
                setOffices(data.data || []);
                setOfficesTotal(data.total || 0);
              }}
            />
          )
        }
        {/* Create/Edit Division Modal */}
        {
          (showCreateDivision || showEditDivision) && (
            <DivisionModal
              division={editingDivision}
              clients={clients}
              onClose={() => {
                setShowCreateDivision(false);
                setShowEditDivision(false);
                setEditingDivision(null);
              }}
              onSave={async () => {
                // Reload divisions
                const q = encodeURIComponent(searchTerm || "");
                const res = await fetch(
                  `${BASE_URL}/api/admin/divisions-list?page=${currentPage}&limit=${pageSize}&search=${q}&sort=${encodeURIComponent(
                    divisionsSortKey
                  )}&dir=${encodeURIComponent(divisionsSortDir)}`,
                  { credentials: "include" }
                );
                const data = await res.json();
                setDivisionsList(data.data || []);
                setDivisionsTotal(data.total || 0);
              }}
            />
          )
        }
        {/* Create/Edit App Note Modal */}
        {
          (showCreateAppNote || showEditAppNote) && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3">
              <div className="bg-card text-foreground rounded-lg shadow w-full max-w-lg max-h-[90vh] overflow-hidden border border-border">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <h3 className="text-lg font-semibold">
                    {editingAppNote ? "Edit App Note" : "Create App Note"}
                  </h3>
                  <button
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setShowCreateAppNote(false);
                      setShowEditAppNote(false);
                      setEditingAppNote(null);
                    }}
                  >
                    ✖
                  </button>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.target;
                    const name = form.name.value;


                    // Get all checked checkboxes with name 'app_pages'
                    const selectedPages = Array.from(
                      form.querySelectorAll('input[name="app_pages"]:checked')
                    ).map((checkbox) => checkbox.value);

                    if (!name.trim()) {
                      showError("Name is required");
                      return;
                    }
                    if (selectedPages.length === 0) {
                      showError("At least one app page type is required");
                      return;
                    }

                    try {
                      const url = editingAppNote
                        ? `${BASE_URL}/api/admin/app-notes/${editingAppNote.app_note_id}`
                        : `${BASE_URL}/api/admin/app-notes`;

                      const res = await fetch(url, {
                        method: editingAppNote ? "PUT" : "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                          name,
                          type: selectedPages,
                          state: "active",
                        }),
                      });

                      const data = await res.json();
                      if (!res.ok)
                        throw new Error(data.message || "Failed to save app note");

                      // Reload app notes (preserve filters)
                      const q = encodeURIComponent(searchTerm || "");
                      const status = encodeURIComponent(
                        selectedSigninStatuses.join(",") || ""
                      );
                      const reloadRes = await fetch(
                        `${BASE_URL}/api/admin/app-notes?page=${currentPage}&limit=${pageSize}&search=${q}&status=${status}` +
                        `&showDeleted=${showDeleted}` +
                        `&sort=${encodeURIComponent(appNotesSortKey)}` +
                        `&dir=${encodeURIComponent(appNotesSortDir)}`,
                        { credentials: "include" }
                      );
                      const reloadData = await reloadRes.json();
                      setAppNotes(mapNotes(reloadData.data || []));
                      setAppNotesTotal(reloadData.total || 0);

                      // Close modal
                      setShowCreateAppNote(false);
                      setShowEditAppNote(false);
                      setEditingAppNote(null);
                    } catch (err) {
                      showError("Error: " + err.message);
                    }
                  }}
                  className="p-4 space-y-4 overflow-y-auto max-h-[65vh]"
                >
                  <div>
                    <label className="block text-sm font-medium mb-2">Name *</label>
                    <input
                      name="name"
                      type="text"
                      defaultValue={editingAppNote?.name || ""}
                      className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
                      placeholder="e.g., I forgot to check in"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      App Pages *
                    </label>
                    <div className="grid grid-cols-2 gap-3 border border-border rounded-lg p-4">
                      {[
                        "Check in",
                        "Check in early",
                        "Check out",
                        "Check out late",
                      ].map((pageType) => (
                        <div key={pageType} className="relative flex items-center">
                          <input
                            type="checkbox"
                            id={`page-${pageType}`}
                            name="app_pages"
                            value={pageType}
                            defaultChecked={editingAppNote?.app_pages
                              ?.split(", ")
                              .includes(pageType)}
                            className="w-5 h-5 text-buttonRed border-gray-300 rounded focus:ring-buttonRed focus:ring-2"
                          />
                          <label
                            htmlFor={`page-${pageType}`}
                            className="ml-3 text-sm text-foreground cursor-pointer hover:text-buttonRed select-none"
                          >
                            {pageType === "Check in early"
                              ? "Check in early (before shift has started)"
                              : pageType === "Check out late"
                                ? "Check out late (after shift has ended)"
                                : pageType}
                          </label>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Select all pages that apply
                    </p>
                  </div>



                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateAppNote(false);
                        setShowEditAppNote(false);
                        setEditingAppNote(null);
                      }}
                      className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-lg"
                    >
                      {editingAppNote ? "Update" : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )
        }
      </fieldset>
    </Layout >
  );
};

// Certification Modal Component
const CertificationModal = ({
  certification,
  divisions = [],
  skills = [],
  onClose,
  onSave,
}) => {
  const toBoolFlag = (value) => {
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "1" || normalized === "true";
    }
    return value === 1 || value === true;
  };

  const buildInitialForm = (cert) => ({
    name: cert?.certification_name || "",
    state: cert?.state || "active",
    description: cert?.description || "",
    expirydate: toBoolFlag(cert?.requires_expiry_date ?? cert?.expirydate),
    file: toBoolFlag(cert?.requires_uploaded_file ?? cert?.file),
    division_ids: cert?.divisions
      ? cert.divisions
        .split(", ")
        .map((d) => divisions.find((div) => div.name === d)?.division_id)
        .filter(Boolean)
      : [],
    skill_ids: cert?.skills
      ? // support new structured array from backend: [{ skillId, skillName, divisions: [] }, ...]
      Array.isArray(cert.skills)
        ? cert.skills
          .map((s) => s.skillId || s.skill_id || s.id)
          .filter(Boolean)
        : // fallback for legacy comma-separated string
        cert.skills
          .split(", ")
          .map((s) => skills.find((skill) => skill.name === s)?.skill_id)
          .filter(Boolean)
      : [],
  });

  const [form, setForm] = useState(buildInitialForm(certification));

  const resetForm = () => setForm(buildInitialForm(certification));
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("certification");
  const [skillApplicability, setSkillApplicability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skillSelection, setSkillSelection] = useState({});
  const [createdCertificationId, setCreatedCertificationId] = useState(
    certification?.id || null
  );
  const DOESNT_APPLY = "doesnt_apply";
  const normalizeApplicability = (value) =>
    value === "optional" || value === "recommended"
      ? value
      : DOESNT_APPLY;

  const certificationId = certification?.id || createdCertificationId;
  const isEditLayout = Boolean(certificationId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return showError("Name is required");

    try {
      setSaving(true);
      const hasCertificationId = Boolean(certificationId);
      const wasCreate = !hasCertificationId;
      const url = hasCertificationId
        ? `${BASE_URL}/api/admin/certifications/${certificationId}`
        : `${BASE_URL}/api/admin/certifications`;

      const method = hasCertificationId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (!res.ok)
        throw new Error(data.message || "Failed to save certification");

      if (wasCreate) {
        setCreatedCertificationId(data.certificationId);
        setActiveTab("applicability");
        showSuccess(
          "Certification created successfully. Continue with skill applicability."
        );
        onSave();
        return;
      }

      showSuccess("Certification updated successfully");
      onSave();
      if (certification) onClose();
    } catch (e) {
      showError("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleDivision = (id) => {
    setForm((prev) => ({
      ...prev,
      division_ids: prev.division_ids.includes(id)
        ? prev.division_ids.filter((d) => d !== id)
        : [...prev.division_ids, id],
    }));
  };

  const toggleSkill = (id) => {
    setForm((prev) => ({
      ...prev,
      skill_ids: prev.skill_ids.includes(id)
        ? prev.skill_ids.filter((s) => s !== id)
        : [...prev.skill_ids, id],
    }));
  };

  const certificationDescriptionToolbar = (
    <span className="ql-formats">
      <select className="ql-header" defaultValue="">
        <option value="1">Heading</option>
        <option value="2">Subheading</option>
        <option value="">Normal</option>
      </select>
      <select className="ql-size" defaultValue="">
        <option value="small">Small</option>
        <option value="">Normal</option>
        <option value="large">Large</option>
        <option value="huge">Huge</option>
      </select>
      <button type="button" className="ql-bold" />
      <button type="button" className="ql-italic" />
      <button type="button" className="ql-underline" />
      <button type="button" className="ql-strike" />
      <select className="ql-align" />
      <button type="button" className="ql-list" value="ordered" />
      <button type="button" className="ql-list" value="bullet" />
      <button type="button" className="ql-indent" value="-1" />
      <button type="button" className="ql-indent" value="+1" />
      <select className="ql-color" />
      <select className="ql-background" />
      <button type="button" className="ql-link" />
      <button type="button" className="ql-image" />
      <button type="button" className="ql-video" />
      <button type="button" className="ql-clean" />
    </span>
  );

  const loadSkillApplicability = async () => {
    if (!certificationId) {
      const skillsForCreate = (skills || []).map((skill) => {
        const skillId = skill.skill_id || skill.id;
        const divisionNames = String(skill.division_name || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
        const divisionIds = String(skill.division_ids || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);

        return {
          skill_id: skillId,
          skill_name: skill.name || "",
          applicability: DOESNT_APPLY,
          divisions: divisionNames.map((name, idx) => ({
            division_id: divisionIds[idx] || `${skillId}-${idx}`,
            division_name: name,
          })),
        };
      });
      setSkillApplicability(skillsForCreate);
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        `${BASE_URL}/api/admin/certifications/${certificationId}/skills`,
        { credentials: "include" }
      );
      const data = await res.json();

      if (data.success) {
        setSkillApplicability(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "applicability") {
      loadSkillApplicability();
    }
  }, [activeTab, certificationId, skills]);

  useEffect(() => {
    const initial = {};
    skillApplicability.forEach((s) => {
      initial[s.skill_id] = normalizeApplicability(s.applicability);
    });
    setSkillSelection(initial);
  }, [skillApplicability]);

  const saveCertificationSkills = async () => {
    let effectiveCertificationId = certificationId;
    if (!effectiveCertificationId) {
      if (!form.name.trim()) {
        showError("Name is required before saving skill applicability");
        return;
      }
      try {
        setSaving(true);
        const createRes = await fetch(`${BASE_URL}/api/admin/certifications`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(form),
        });
        const createData = await createRes.json();
        if (!createRes.ok) {
          throw new Error(createData.message || "Failed to create certification");
        }
        effectiveCertificationId = createData.certificationId;
        setCreatedCertificationId(effectiveCertificationId);
        onSave();
      } catch (e) {
        showError("Error: " + e.message);
        return;
      } finally {
        setSaving(false);
      }
    }

    const persistedSkills = Object.fromEntries(
      Object.entries(skillSelection).filter(
        ([, applicability]) =>
          applicability === "optional" || applicability === "recommended"
      )
    );

    await fetch(
      `${BASE_URL}/api/admin/certifications/${effectiveCertificationId}/skills`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ skills: persistedSkills }),
      }
    );

    await loadSkillApplicability(); // 🔥 refresh after save
    showSuccess("Certification skills saved");
  };

  const handleSave = saveCertificationSkills;

  // const handleSave = async () => {
  //   if (hasExistingSkills) {
  //     await updateCertificationSkills();
  //   } else {
  //     await createCertificationSkills();
  //   }

  //   showSuccess("Certification skills saved");
  // };

  // const createCertificationSkills = async () => {
  //   await fetch(
  //     `${BASE_URL}/api/admin/certifications/${certificationId}/skills`,
  //     {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       credentials: "include",
  //       body: JSON.stringify({
  //         skills: skillSelection,
  //       }),
  //     }
  //   );
  // };

  const updateCertificationSkills = async () => {
    await fetch(
      `${BASE_URL}/api/admin/certifications/${certificationId}/skills`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          skills: skillSelection,
        }),
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3">
      <div className="bg-card text-foreground rounded-lg shadow w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border">
        {!isEditLayout ? (
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-lg font-semibold">Create Certification</h3>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={onClose}
            >
              ✖
            </button>
          </div>
        ) : (
          <div className="border-b border-border">
            <div className="flex items-center justify-between px-4 py-3">
              <h3 className="text-lg font-semibold">Edit Certification</h3>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={onClose}
              >
                ✖
              </button>
            </div>
            <div className="flex gap-6 px-4 pb-3">
              <button
                type="button"
                onClick={() => setActiveTab("certification")}
                className={`pb-2 text-sm font-medium ${activeTab === "certification"
                  ? "border-b-2 border-buttonRed text-buttonRed"
                  : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                SETTINGS
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("applicability")}
                className={`pb-2 text-sm font-medium ${activeTab === "applicability"
                  ? "border-b-2 border-buttonRed text-buttonRed"
                  : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                SKILL APPLICABILITY
              </button>
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="p-4 space-y-4 overflow-y-auto max-h-[65vh]"
        >
          {activeTab === "certification" && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
                  placeholder="e.g., CPR Level C, WHMIS, Forklift License"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Description
                </label>
                <Editor
                  value={form.description || ""}
                  onTextChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.htmlValue || "",
                    }))
                  }
                  style={{ minHeight: "220px" }}
                  headerTemplate={certificationDescriptionToolbar}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Requires</label>
                <div className="space-y-2 rounded-lg border border-border p-3 bg-card">
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={Boolean(form.expirydate)}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          expirydate: e.target.checked,
                        }))
                      }
                      className="accent-[#DC1F00]"
                    />
                    <span>An Expiry Date</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={Boolean(form.file)}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          file: e.target.checked,
                        }))
                      }
                      className="accent-[#DC1F00]"
                    />
                    <span>An Uploaded File</span>
                  </label>
                </div>
              </div>

            </>
          )}
          {activeTab === "applicability" && (
            <div className="space-y-4">
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : (
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left px-3 py-2">Skill</th>
                        <th className="text-left px-3 py-2">
                          Applicable Divisions
                        </th>
                        <th className="text-center px-3 py-2">
                          doesn't apply
                        </th>
                        <th className="text-center px-3 py-2">Optional</th>
                        <th className="text-center px-3 py-2">Recommended</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skillApplicability.map((skill) => (
                        <tr key={skill.skill_id} className="border-t">
                          {/* Skill name */}
                          <td className="px-3 py-2 font-medium">
                            {skill.skill_name}
                          </td>

                          {/* Divisions */}
                          <td className="px-3 py-2">
                            {skill.divisions.length === 0 ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {skill.divisions.map((div) => (
                                  <span
                                    key={div.division_id}
                                    className="px-2 py-0.5 rounded bg-secondary text-xs"
                                  >
                                    {div.division_name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>

                          {/* Doesn't apply */}
                          <td className="px-3 py-2 text-center">
                            <input
                              type="radio"
                              name={`skill-${skill.skill_id}`}
                              checked={
                                (skillSelection[skill.skill_id] || DOESNT_APPLY) ===
                                DOESNT_APPLY
                              }
                              onChange={() =>
                                setSkillSelection((prev) => ({
                                  ...prev,
                                  [skill.skill_id]: DOESNT_APPLY,
                                }))
                              }
                            />
                          </td>

                          {/* Optional */}
                          <td className="px-3 py-2 text-center">
                            <input
                              type="radio"
                              name={`skill-${skill.skill_id}`}
                              checked={
                                skillSelection[skill.skill_id] === "optional"
                              }
                              onChange={() =>
                                setSkillSelection((prev) => ({
                                  ...prev,
                                  [skill.skill_id]: "optional",
                                }))
                              }
                            />
                          </td>

                          {/* Recommended */}
                          <td className="px-3 py-2 text-center">
                            <input
                              type="radio"
                              name={`skill-${skill.skill_id}`}
                              checked={
                                skillSelection[skill.skill_id] === "recommended"
                              }
                              onChange={() =>
                                setSkillSelection((prev) => ({
                                  ...prev,
                                  [skill.skill_id]: "recommended",
                                }))
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* <div>
            <label className="block text-sm font-medium mb-2">Divisions</label>
            <div className="border border-border rounded-lg p-3 max-h-32 overflow-y-auto">
              {divisions.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  No divisions available
                </span>
              ) : (
                <div className="space-y-2">
                  {divisions.map((div) => (
                    <label
                      key={div.division_id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form.division_ids.includes(div.division_id)}
                        onChange={() => toggleDivision(div.division_id)}
                        className="accent-[#DC1F00]"
                      />
                      <span>{div.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Skills (Optional)
            </label>
            <div className="border border-border rounded-lg p-3 max-h-32 overflow-y-auto">
              {skills.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  No skills available
                </span>
              ) : (
                <div className="space-y-2">
                  {skills.map((skill) => (
                    <label
                      key={skill.skill_id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form.skill_ids.includes(skill.skill_id)}
                        onChange={() => toggleSkill(skill.skill_id)}
                        className="accent-[#DC1F00]"
                      />
                      <span>{skill.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div> */}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg"
            >
              Close
            </button>

            {activeTab === "certification" && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-border rounded-lg"
              >
                Cancel
              </button>
            )}

            {activeTab === "certification" && (
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-buttonRed text-white rounded-lg"
              >
                {saving
                  ? "Saving..."
                  : certification
                    ? "Update"
                    : certificationId
                      ? "Save"
                      : "Create"}
              </button>
            )}

            {activeTab === "applicability" && (
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 bg-buttonRed text-white rounded-lg"
              >
                Save Skills
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// Position Modal Component
const PositionModal = ({
  position,
  divisions = [],
  skills = [],
  certifications = [],
  onClose,
  onSave,
}) => {
  const getCertId = (c) =>
    c?.id ?? c?.certification_id ?? c?.certificationId ?? c?.cert_id;
  const getCertName = (c) =>
    c?.certification_name ?? c?.name ?? c?.certificationName ?? "";
  const [loading, setLoading] = useState(Boolean(position?.id));
  const [form, setForm] = useState({
    name: position?.position_name || "",
    state: position?.state || "active",
    division_ids: [],
    skill_ids: [],
    certification_ids: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!position?.id) return;

    let cancelled = false;
    const loadPosition = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${BASE_URL}/api/admin/positions/${position.id}`,
          { credentials: "include" }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to load position");
        if (cancelled) return;

        setForm({
          name: data.data.position_name || "",
          state: data.data.state || "active",
          division_ids: data.data.division_ids || [],
          skill_ids: data.data.skill_ids || [],
          certification_ids: data.data.certification_ids || [],
        });
      } catch (e) {
        if (!cancelled) showError("Error: " + e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPosition();
    return () => {
      cancelled = true;
    };
  }, [position?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return showError("Name is required");

    try {
      setSaving(true);
      const url = position
        ? `${BASE_URL}/api/admin/positions/${position.id}`
        : `${BASE_URL}/api/admin/positions`;

      const method = position ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save position");

      showSuccess(
        position
          ? "Position updated successfully"
          : "Position created successfully"
      );
      onSave();
      onClose();
    } catch (e) {
      showError("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleDivision = (id) => {
    setForm((prev) => ({
      ...prev,
      division_ids: prev.division_ids.includes(id)
        ? prev.division_ids.filter((d) => d !== id)
        : [...prev.division_ids, id],
    }));
  };

  const toggleSkill = (id) => {
    setForm((prev) => ({
      ...prev,
      skill_ids: prev.skill_ids.includes(id)
        ? prev.skill_ids.filter((s) => s !== id)
        : [...prev.skill_ids, id],
    }));
  };

  const toggleCertification = (id) => {
    setForm((prev) => ({
      ...prev,
      certification_ids: prev.certification_ids.includes(id)
        ? prev.certification_ids.filter((c) => c !== id)
        : [...prev.certification_ids, id],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3">
      <div className="bg-card text-foreground rounded-lg shadow w-full max-w-3xl max-h-[90vh] overflow-hidden border border-border">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-lg font-semibold">
            {position ? "Edit Position" : "Create Position"}
          </h3>
          <button
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            ✖
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 space-y-4 overflow-y-auto max-h-[65vh]"
        >
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading position...
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
                  placeholder="e.g., General Laborer, Supervisor, Hostess"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Divisions
                  </label>
                  <div className="border border-border rounded-lg p-3 max-h-32 overflow-y-auto">
                    {divisions.length === 0 ? (
                      <span className="text-sm text-muted-foreground">
                        No divisions available
                      </span>
                    ) : (
                      <div className="space-y-2">
                        {divisions.map((div) => (
                          <label
                            key={div.division_id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={form.division_ids.includes(div.division_id)}
                              onChange={() => toggleDivision(div.division_id)}
                              className="accent-[#DC1F00]"
                            />
                            <span>{div.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Skills</label>
                  <div className="border border-border rounded-lg p-3 max-h-32 overflow-y-auto">
                    {skills.length === 0 ? (
                      <span className="text-sm text-muted-foreground">
                        No skills available
                      </span>
                    ) : (
                      <div className="space-y-2">
                        {skills.map((skill) => (
                          <label
                            key={skill.id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={form.skill_ids.includes(skill.id)}
                              onChange={() => toggleSkill(skill.id)}
                              className="accent-[#DC1F00]"
                            />
                            <span>{skill.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Certifications
                  </label>
                  <div className="border border-border rounded-lg p-3 max-h-32 overflow-y-auto">
                    {certifications.length === 0 ? (
                      <span className="text-sm text-muted-foreground">
                        No certifications available
                      </span>
                    ) : (
                      <div className="space-y-2">
                        {certifications.map((cert) => {
                          const cid = getCertId(cert);
                          return (
                            <label
                              key={cid ?? getCertName(cert)}
                              className="flex items-center gap-2 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={
                                  cid ? form.certification_ids.includes(cid) : false
                                }
                                onChange={() => cid && toggleCertification(cid)}
                                className="accent-[#DC1F00]"
                              />
                              <span>{getCertName(cert)}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary"
                >
                  Cancel
                </button>
                <button
                  disabled={saving}
                  type="submit"
                  className="px-4 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-lg disabled:opacity-50"
                >
                  {saving ? "Saving..." : position ? "Update" : "Create"}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

// Division Modal Component
const DivisionModal = ({ division = [], onClose, onSave }) => {
  // console.log("DivisionModal - clients prop:", clients);
  // console.log("DivisionModal - clients length:", clients?.length);

  const [form, setForm] = useState({
    name: division?.division_name || "",
    // client_ids: division?.clients
    //   ? division.clients
    //       .split(", ")
    //       .map((c) => clients.find((client) => client.name === c)?.client_id)
    //       .filter(Boolean)
    //   : [],
    availability_mode: division?.availability_mode === "automatic" ? "automatic" : "manual",
    state: division?.state || "active",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return showError("Name is required");
    // if (!form.client_ids || form.client_ids.length === 0) return showError('At least one client is required');

    try {
      setSaving(true);
      const url = division
        ? `${BASE_URL}/api/admin/divisions/${division.id}`
        : `${BASE_URL}/api/admin/divisions`;

      const method = division ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save division");

      showSuccess(
        division
          ? "Division updated successfully"
          : "Division created successfully"
      );
      onSave();
      onClose();
    } catch (e) {
      showError("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleClient = (id) => {
    setForm((prev) => ({
      ...prev,
      client_ids: prev.client_ids.includes(id)
        ? prev.client_ids.filter((c) => c !== id)
        : [...prev.client_ids, id],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3">
      <div className="bg-card text-foreground rounded-lg shadow w-full max-w-lg max-h-[90vh] overflow-hidden border border-border">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-lg font-semibold">
            {division ? "Edit Division" : "Create Division"}
          </h3>
          <button
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            ✖
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 space-y-4 overflow-y-auto max-h-[65vh]"
        >
          <div>
            <label className="block text-sm font-medium mb-2">
              Division Name *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
              placeholder="e.g., Main, Auto Division, Healthcare"
              required
            />
          </div>

          {/* <div>
            <label className="block text-sm font-medium mb-2">
              Client(s) *
            </label>
            <div className="border border-border rounded-lg p-3 max-h-32 overflow-y-auto">
              {console.log("Rendering clients:", clients)}
              {clients.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  No clients available
                </span>
              ) : (
                <div className="space-y-2">
                  {clients.map((client) => (
                    <label
                      key={client.client_id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form.client_ids.includes(client.client_id)}
                        onChange={() => toggleClient(client.client_id)}
                        className="accent-[#DC1F00]"
                      />
                      <span>{client.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div> */}

          <div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.availability_mode === "manual"}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    availability_mode: e.target.checked
                      ? "manual"
                      : "automatic",
                  }))
                }
                className="w-4 h-4"
              />

              <label className="text-sm font-medium">
                Send Temp Staff in this Division weekly availability
                confirmations
              </label>
            </div>

            {/* <select
              value={form.availability_mode}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  availability_mode: e.target.value,
                }))
              }
              className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
            >
              <option value="manual">Manual</option>
              <option value="automatic">Automatic</option>
            </select> */}
          </div>

          {/* <div>
            <label className="block text-sm font-medium mb-2">Status</label>
            <select
              value={form.state}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, state: e.target.value }))
              }
              className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div> */}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              type="submit"
              className="px-4 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-lg disabled:opacity-50"
            >
              {saving ? "Saving..." : division ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// App Note Modal Component
const AppNoteModal = ({ appNote, onClose, onSave }) => {
  // Parse existing app_pages string into array if needed
  const initialPages = appNote?.app_pages
    ? Array.isArray(appNote.app_pages)
      ? appNote.app_pages
      : appNote.app_pages.split(", ")
    : [];

  const [form, setForm] = useState({
    name: appNote?.name || "",
    app_pages: initialPages,
    state: appNote?.state || "active",
  });
  const [saving, setSaving] = useState(false);

  const pageOptions = [
    "Check in",
    "Check in early",
    "Check out",
    "Check out late",
  ];

  const togglePage = (page) => {
    setForm((prev) => {
      const current = prev.app_pages || [];
      if (current.includes(page)) {
        return { ...prev, app_pages: current.filter((p) => p !== page) };
      } else {
        return { ...prev, app_pages: [...current, page] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return showError("Name is required");
    if (form.app_pages.length === 0)
      return showError("At least one App Page is required");

    try {
      setSaving(true);
      // Determine ID (handle potential property mismatch)
      const id = appNote?.id || appNote?.app_note_id || appNote?.checkin_note_id;
      const url = appNote
        ? `${BASE_URL}/api/admin/app-notes/${id}`
        : `${BASE_URL}/api/admin/app-notes`;

      const method = appNote ? "PUT" : "POST";

      const payload = {
        name: form.name,
        type: form.app_pages, // Backend expects 'type' array
        state: form.state,
      };

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save app note");

      showSuccess(
        appNote
          ? "App note updated successfully"
          : "App note created successfully"
      );
      onSave(); // Refreshes list?
      onClose();
    } catch (e) {
      showError("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3">
      <div className="bg-card text-foreground rounded-lg shadow w-full max-w-lg max-h-[90vh] overflow-hidden border border-border">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-lg font-semibold">
            {appNote ? "Edit App Note" : "Create App Note"}
          </h3>
          <button
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            ✖
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 space-y-4 overflow-y-auto max-h-[65vh]"
        >
          <div>
            <label className="block text-sm font-medium mb-2">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
              placeholder="e.g., I forgot to check in"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              App Pages *
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              Select all pages that apply
            </p>
            <div className="space-y-2 border border-border rounded-lg p-3">
              {pageOptions.map((opt) => (
                <label
                  key={opt}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={form.app_pages.includes(opt)}
                    onChange={() => togglePage(opt)}
                    className="accent-[#DC1F00] w-4 h-4"
                  />
                  <span>
                    {opt === "Check in early"
                      ? "Check in early (before shift has started)"
                      : opt === "Check out late"
                        ? "Check out late (after shift has ended)"
                        : opt}
                  </span>
                </label>
              ))}
            </div>
          </div>



          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              type="submit"
              className="px-4 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-lg disabled:opacity-50"
            >
              {saving ? "Saving..." : appNote ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
